// @ts-nocheck
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';

import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import {
  MapPin, BarChart3, X, Filter, Plus, Download, Calendar, User, FileText,
  ChevronDown, Check, Search, Hash, AlignLeft, ListChecks, Layers, Route,
  Sparkles, TrendingUp, Activity, Gauge, Settings, Pencil,
  Copy, Trash2, Clock, Eye, ArrowRight, Info, Ban, ScatterChart as ScatterIcon,
  BarChart2, Upload, UserCircle2, AlertTriangle, Maximize, Minimize, RefreshCw,
} from 'lucide-react';

import { FormResponse, FormMetadata, FormVersion, KPICalcType, ChartType, Mode, CardConfig } from '../../types';
import { MARKER_COLORS, deg2rad, getDistanceKm, MAP_LAYERS, CHART_COLORS, CALC_LABELS, VIZ_META } from './InsightsConstants';
import { evaluateParameterFormula, injectDynamicParameters, ContainerDef, isTimeString, timeToSeconds, secondsToTime } from './ParameterConstructor/engine';
import { cn } from '../lib/utils';
import { FilterBar, useClickOutside, PopoverHeader } from './SharedFilterBar';
import { useAuth } from '@/src/context/AuthContext';
import { ErrorBoundary } from './ErrorBoundary';
import { useChartSerializer } from '../utils/chartSerializer';

const ChartRenderer = React.lazy(() => import('./ChartRenderer'));
const MapRenderer = React.lazy(() => import('./MapRenderer'));
import AliasBuilder from './AliasBuilder';

/* =========================================================
   PERSISTENCE (localStorage)
   ========================================================= */
const STORAGE_VERSION = 5; // Updated cache version // CACHE_RESET_FLAG_V2: Bumping version to invalidate old configurations bound to raw forms
function storageKey(mode: Mode, tenantId: string | undefined) {
  return `insights-view::${tenantId || 'global'}::${mode}::v${STORAGE_VERSION}`;
}
function loadPersisted(mode: Mode, tenantId: string | undefined): any {
  try {
    const raw = localStorage.getItem(storageKey(mode, tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch { /* ignore */ }
  return null;
}
function savePersisted(mode: Mode, tenantId: string | undefined, state: any) {
  try {
    localStorage.setItem(storageKey(mode, tenantId), JSON.stringify(state));
  } catch { /* quota exceeded or disabled */ }
}

const MAP_LAYERS = [
  { id: 'standard', name: 'Padrão', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' },
  { id: 'satellite', name: 'Satélite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}' },
  { id: 'dark', name: 'Escuro', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' }
];
const CHART_COLORS = ['#2563eb', '#f97316', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#f59e0b', '#ec4899'];

const CALC_LABELS: Record<KPICalcType, string> = {
  sum: 'Soma',
  avg: 'Média',
  count: 'Contagem',
  count_unique: 'Contar Únicos',
  delta: 'Variação (máx − mín)',
  max: 'Máximo',
  min: 'Mínimo',
  mode: 'Mais frequente',
  median: 'Mediana',
};


/* =========================================================
   HELPERS
   ========================================================= */
export const getFieldValue = (r: any, fId: string | undefined, allFields?: any[]) => {
  if (!fId) return undefined;
  if (fId.startsWith('GLOBAL::')) {
     const globalField = allFields?.find((f: any) => f.fieldId === fId);
     const linkedIds = globalField?.linkedFieldIds || [];
     for (const lid of linkedIds) {
        let lReqFormId: string | null = null;
        let lReqFieldId = lid;
        if (lid.includes('::')) {
            [lReqFormId, lReqFieldId] = lid.split('::');
        }
        if (lReqFormId && lReqFormId !== 'global') {
            const rFormId = r.formId || r.form_id;
            if (rFormId !== lReqFormId) continue;
        }
        const val = r.data?.[lReqFieldId]?.value;
        if (val !== undefined && val !== null && val !== '') return val;
     }
     return undefined;
  }

  let reqFormId: string | null = null;
  let reqFieldId = fId;
  if (fId.includes('::') && !fId.startsWith('param_')) {
     [reqFormId, reqFieldId] = fId.split('::');
  }

  if (reqFormId && reqFormId !== 'global') {
      const rowFormId = r.formId || r.form_id;
      if (rowFormId !== reqFormId) return undefined;
  }

  return r.data?.[reqFieldId]?.value;
};

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

function EnumListEditor({ value, onChange }: { value: string, onChange: (val: string) => void }) {
  const [items, setItems] = useState<string[]>(() => value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : []);
  const [newItem, setNewItem] = useState('');
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);

  useEffect(() => {
    onChange(items.join(', '));
  }, [items]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1">
         <input 
            type="text"
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => {
               if (e.key === 'Enter' && newItem.trim()) {
                  setItems([...items, newItem.trim()]);
                  setNewItem('');
               }
            }}
            placeholder="Adicionar item..."
            className="flex-1 text-xs border-gray-300 rounded focus:ring-emerald-400 py-1"
         />
         <button 
            onClick={() => {
               if (newItem.trim()) {
                  setItems([...items, newItem.trim()]);
                  setNewItem('');
               }
            }}
            className="bg-emerald-100 text-emerald-700 px-2 rounded hover:bg-emerald-200 transition-colors flex items-center justify-center"
         >
            <Plus size={14} />
         </button>
      </div>
      <div className="flex flex-col gap-1 max-h-40 overflow-y-auto custom-scrollbar pr-1">
        {items.length === 0 && <span className="text-[10px] text-gray-400 italic">Nenhum item na lista</span>}
        {items.map((item, idx) => (
           <div 
             key={item + idx}
             draggable
             onDragStart={(e) => {
                setDraggedIdx(idx);
                e.dataTransfer.effectAllowed = "move";
             }}
             onDragOver={(e) => e.preventDefault()}
             onDrop={(e) => {
                e.preventDefault();
                if (draggedIdx === null || draggedIdx === idx) return;
                const newItems = [...items];
                const draggedItem = newItems.splice(draggedIdx, 1)[0];
                newItems.splice(idx, 0, draggedItem);
                setItems(newItems);
                setDraggedIdx(null);
             }}
             className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded p-1 cursor-grab active:cursor-grabbing hover:border-emerald-300 shadow-sm"
           >
              <div className="flex items-center gap-2 overflow-hidden">
                 <div className="text-gray-400 cursor-grab px-0.5">
                    <AlignLeft size={10} />
                 </div>
                 <span className="text-[11px] font-semibold text-gray-700 truncate"><span className="text-gray-400 mr-1">{idx + 1}.</span>{item}</span>
              </div>
              <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500 shrink-0 p-1">
                 <X size={12} />
              </button>
           </div>
        ))}
      </div>
    </div>
  );
}

function GlobalInputsModal({ open, onClose, globalInputs, setGlobalInputs }: any) {
  const ref = useClickOutside<HTMLDivElement>(onClose);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog" aria-modal="true" aria-label="Variáveis Globais">
      <div ref={ref} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Settings size={18} className="text-emerald-600" />
              Variáveis Globais (Inputs Dinâmicos)
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Crie valores padrão para usar dentro de seus Parâmetros (Ex: Horário de Início).
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 bg-gray-50 hover:bg-gray-100 p-2 rounded-full transition-colors border border-gray-100">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto bg-gray-50 custom-scrollbar">
          <div className="flex items-center justify-end mb-4">
             <button onClick={() => setGlobalInputs([...globalInputs, { id: 'var_' + Date.now(), name: 'Nova Variável', type: 'time', value: '08:00:00' }])}
                className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors shadow-sm">
                <Plus size={14} /> Adicionar Variável
             </button>
          </div>
          
          {globalInputs.length === 0 ? (
             <div className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-gray-200 rounded-xl bg-white">
                <Settings size={32} className="text-gray-300 mb-3" />
                <p className="text-sm font-semibold text-gray-500">Nenhuma variável criada</p>
                <p className="text-xs text-gray-400">Clique acima para criar seu primeiro input dinâmico.</p>
             </div>
          ) : (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {globalInputs.map((input: any, idx: number) => (
                   <div key={input.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-3 relative group shadow-sm hover:border-emerald-300 transition-colors">
                      <div className="flex justify-between items-center gap-2">
                         <input 
                            type="text" 
                            value={input.name} 
                            onChange={(e) => {
                                const n = [...globalInputs]; n[idx].name = e.target.value; setGlobalInputs(n);
                            }}
                            placeholder="Nome do Input"
                            className="text-sm font-bold bg-transparent border-none focus:ring-1 focus:ring-emerald-400 p-1 text-gray-800 w-full rounded" 
                         />
                         <button onClick={() => setGlobalInputs(globalInputs.filter((g: any) => g.id !== input.id))} 
                            className="text-gray-400 hover:text-red-500 transition-colors bg-gray-50 hover:bg-red-50 p-1.5 rounded"
                            title="Remover variável">
                            <Trash2 size={14} />
                         </button>
                      </div>
                      
                      <div className="flex items-center gap-2">
                         <div className="flex-1">
                             <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Tipo</label>
                             <select 
                                value={input.type}
                                onChange={(e) => {
                                    const n = [...globalInputs]; n[idx].type = e.target.value; setGlobalInputs(n);
                                }}
                                className="w-full text-xs border-gray-300 rounded focus:ring-emerald-400 py-1.5"
                             >
                                <option value="number">Decimal</option>
                                <option value="text">Texto</option>
                                <option value="time">Duração/Hora</option>
                                <option value="date">Data</option>
                                <option value="enumlist">Lista de Opções</option>
                             </select>
                         </div>
                         <div className="flex-1">
                             <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">
                                {input.type === 'enumlist' ? 'Ordem da Lista' : 'Valor Inicial'}
                             </label>
                             {input.type === 'enumlist' ? (
                               <EnumListEditor 
                                  value={input.value || ''}
                                  onChange={(val) => {
                                      const n = [...globalInputs]; n[idx].value = val; setGlobalInputs(n);
                                  }}
                               />
                             ) : (
                               <input 
                                  type={input.type === 'time' ? 'text' : input.type === 'number' ? 'number' : input.type === 'date' ? 'date' : 'text'} 
                                  placeholder="Ex: 08:00:00"
                                  step={input.type === 'number' ? "0.01" : undefined}
                                  value={input.value || ''}
                                  onChange={(e) => {
                                      const n = [...globalInputs]; n[idx].value = e.target.value; setGlobalInputs(n);
                                  }}
                                  className="w-full text-xs border-gray-300 rounded focus:ring-emerald-400 py-1.5"
                               />
                             )}
                         </div>
                      </div>
                      
                      <div className="mt-2 bg-gray-50 rounded-lg p-2 flex items-center justify-between border border-gray-100">
                         <span className="text-[10px] text-gray-500 font-medium">Chave para uso na fórmula:</span>
                         <span className="text-[10px] font-mono font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200 user-select-all">
                            {input.id}
                         </span>
                      </div>
                   </div>
                ))}
             </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InsightsView({
  mode,
  responses,
  forms,
  versions,
}: {
  mode: Mode;
  responses: FormResponse[];
  forms: FormMetadata[];
  versions: FormVersion[];
}) {
  const accent = mode === 'map' ? 'blue' : 'emerald';
  const accentColors = mode === 'map'
    ? { text: 'text-blue-700', bg: 'bg-blue-600', bgLight: 'bg-blue-50', border: 'border-blue-200', ring: 'focus:ring-blue-400', hover: 'hover:bg-blue-700' }
    : { text: 'text-emerald-700', bg: 'bg-emerald-600', bgLight: 'bg-emerald-50', border: 'border-emerald-200', ring: 'focus:ring-emerald-400', hover: 'hover:bg-emerald-700' };

  /* ---------- CURRENT USER (for card signature in analytics mode) ---------- */
  const { user: currentUser } = useAuth();
  const tenantId = currentUser?.tenantId;

  /* ---------- PERSISTED STATE (localStorage) ---------- */
  const persisted = useMemo(() => loadPersisted(mode, tenantId), [mode, tenantId]);

  /* ---------- FILTERS ---------- */
  const allowedForms = ['vw-lavagens-cestos', 'form-1775766905782', 'form-1775762620276'];
  const [selectedForms, setSelectedForms] = useState<string[]>(
    () => (persisted?.selectedForms as string[] | undefined) ?? ['vw-lavagens-cestos', 'form-1775766905782', 'form-1775762620276']
  );
  const [selectedRespondent, setSelectedRespondent] = useState<string>(persisted?.selectedRespondent ?? 'all');
  const [startDate, setStartDate] = useState(persisted?.startDate ?? '');
  const [endDate, setEndDate] = useState(persisted?.endDate ?? '');
  const [dynamicFilters, setDynamicFilters] = useState<Record<string, any>>(persisted?.dynamicFilters ?? {});

  /* ---------- MAP ---------- */
  const [mapLayer, setMapLayer] = useState(() => {
    const savedId = persisted?.mapLayerId;
    return MAP_LAYERS.find(l => l.id === savedId) || MAP_LAYERS[0];
  });
  const [pinLegend1, setPinLegend1] = useState<string>(persisted?.pinLegend1 ?? 'startedAt');
  const [pinLegend2, setPinLegend2] = useState<string>(persisted?.pinLegend2 ?? '');
  const [showTimeline, setShowTimeline] = useState(false);
  const [correctionFactor, setCorrectionFactor] = useState<number>(persisted?.correctionFactor ?? 1.6);
  const [highlightedResponseId, setHighlightedResponseId] = useState<string | null>(null);
  const [flyToPos, setFlyToPos] = useState<[number, number] | null>(null);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);

  /* ---------- CARDS (unified KPI + Charts) ---------- */
  const [cards, setCards] = useState<CardConfig[]>(persisted?.cards ?? []);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<CardConfig | null>(null);
  const [importPayload, setImportPayload] = useState<any | null>(null); // for import-choice dialog
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ---------- GLOBAL ALIASES (TICKET 2) ---------- */
  const [globalAliases, setGlobalAliases] = useState<{ id: string, name: string, linkedFieldIds: string[] }[]>(() => {
    try {
       const str = localStorage.getItem('insights-global-aliases');
       if (str) return JSON.parse(str);
    } catch {}
    return [];
  });
  const [aliasBuilderOpen, setAliasBuilderOpen] = useState(false);

  /* ---------- GLOBAL INPUTS (TICKET 3) ---------- */
  const [globalInputs, setGlobalInputs] = useState<{ id: string, name: string, type: string, value: any }[]>(() => {
    try {
       const str = localStorage.getItem('insights-global-inputs');
       if (str) return JSON.parse(str);
    } catch {}
    return [];
  });
  const [globalInputsOpen, setGlobalInputsOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    localStorage.setItem('insights-global-inputs', JSON.stringify(globalInputs));
  }, [globalInputs]);

  // Analytics Tabs
  const uniqueTabs = useMemo(() => {
     const t = new Set<string>();
     cards.forEach((c: CardConfig) => {
         if (c.viz !== 'kpi') t.add(c.tab || 'Geral');
     });
     if (t.size === 0) t.add('Geral');
     return Array.from(t).sort();
  }, [cards]);

  const [activeTab, setActiveTab] = useState<string>('Geral');

  // Ensure active tab is valid
  useEffect(() => {
      if (!uniqueTabs.includes(activeTab) && uniqueTabs.length > 0) {
          setActiveTab(uniqueTabs[0]);
      }
  }, [uniqueTabs, activeTab]);

  /* ---------- Persist whenever something relevant changes ---------- */
  useEffect(() => {
    const base: any = {
      cards,
      dynamicFilters,
      selectedForms,
      selectedRespondent,
      startDate,
      endDate,
    };
    if (mode === 'map') {
      base.mapLayerId = mapLayer.id;
      base.pinLegend1 = pinLegend1;
      base.pinLegend2 = pinLegend2;
      base.correctionFactor = correctionFactor;
    }
    savePersisted(mode, tenantId, base);
  }, [mode, tenantId, cards, dynamicFilters, selectedForms, selectedRespondent, startDate, endDate, mapLayer, pinLegend1, pinLegend2, correctionFactor]);

  /* keep selectedForms synced with forms prop */
  useEffect(() => {
    localStorage.setItem('insights-global-aliases', JSON.stringify(globalAliases));
  }, [globalAliases]);

  useEffect(() => {
    setSelectedForms(prev => {
      const newIds = forms.map(f => f.id);
      if (prev.length === 0) return newIds;
      const still = newIds.filter(id => prev.includes(id));
      return still.length > 0 ? still : newIds;
    });
  }, [forms]);

  /* ---------- all fields ----------
     Consolidates the same question across versions:
     - Group by (formId, fieldId)
     - Keep the label / type / options from the MOST RECENT version (highest version number)
     - If the fieldId changes between versions, they ARE different questions (keep separate).
  */
/* ---------- CUSTOM PARAMETERS ---------- */
  const customParameters = useMemo(() => { try { const str = localStorage.getItem('@forms-asa-branca/custom-parameters') || localStorage.getItem('asa_branca_custom_parameters'); if (str) return JSON.parse(str) as any[]; } catch(e) {} return []; }, []);

  const allFields = useMemo(() => {
    const byKey = new Map<string, {
      formId: string;
      formTitle: string;
      fieldId: string;
      fieldLabel: string;
      fieldType: string;
      options: any;
      _version: number;
    }>();
    (versions || []).forEach((fv: any) => {
      const formId = fv.formId || fv.form_id;
      const form = forms.find((f: any) => f.id === formId);
      const vNum = typeof fv.version === 'number' ? fv.version : 0;
      (fv.schema || []).forEach((field: any) => {
        const key = `${formId}::${field.id}`;
        const prev = byKey.get(key);
        if (!prev || vNum > prev._version) {
          byKey.set(key, {
            formId,
            formTitle: form?.title || 'Formulário',
            fieldId: key,
            fieldLabel: field.label,
            fieldType: field.type,
            options: field.options,
            _version: vNum,
          });
        }
      });
    });

    const fields = Array.from(byKey.values()).map(({ _version, ...rest }) => rest);

    // Inject Custom Parameters as selectable fields
    if (customParameters && customParameters.length > 0) {
      customParameters.forEach(param => {
         fields.push({
            formId: param.targetForm || (param as any)._metadata?.targetForm || 'global',
            formTitle: 'Parâmetro Customizado',
            fieldId: param.id,
            fieldLabel: `[PC] ${param.name}`,
            fieldType: (param as any).returnType || 'number',
            options: null
         });
      });
    }

    // Inject Global Aliases (TICKET 2)
    globalAliases.forEach(alias => {
        if (alias.linkedFieldIds.length > 0) {
            const firstMapped = fields.find(f => f.fieldId === alias.linkedFieldIds[0] || f.fieldId.endsWith('::' + alias.linkedFieldIds[0]));
            fields.push({
               formId: 'global',
               formTitle: 'Agrupador Global',
               fieldId: 'GLOBAL::' + alias.id,
               fieldLabel: `[Global] ${alias.name}`,
               fieldType: firstMapped?.fieldType || 'text',
               options: firstMapped?.options,
               linkedFieldIds: alias.linkedFieldIds
            } as any);
        }
    });

    return fields;
  }, [versions, forms, customParameters]);

  const formColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    forms.forEach((f, i) => { map[f.id] = MARKER_COLORS[i % MARKER_COLORS.length]; });
    return map;
  }, [forms]);

  /* ---------- filter logic ----------
     STEP 1: Pure, fast synchronous filter — never blocks the UI thread.
     injectDynamicParameters (O(N×P)) is intentionally NOT called here.
  ---------- */
  const _baseFiltered = useMemo(() => {
    const filtered = responses.filter(r => {
      if (mode === 'map' && !r.deviceInfo?.geolocation) return false;
      if (!selectedForms.includes(r.formId)) return false;
      if (selectedRespondent !== 'all' && r.respondentId !== selectedRespondent) return false;

      const rDate = new Date(r.startedAt);
      if (startDate) {
        const [year, month, day] = startDate.split('-').map(Number);
        const dStart = new Date(year, month - 1, day, 0, 0, 0, 0);
        if (rDate < dStart) return false;
      }
      if (endDate) {
        const [year, month, day] = endDate.split('-').map(Number);
        const dEnd = new Date(year, month - 1, day, 23, 59, 59, 999);
        if (rDate > dEnd) return false;
      }

      for (const [fieldId, filterObj] of Object.entries(dynamicFilters)) {
        if (!filterObj || Object.keys(filterObj).length === 0) continue;
        const ans = getFieldValue(r, fieldId, allFields);
        const fieldConfig = allFields.find(f => f.fieldId === fieldId);
        if (!fieldConfig) continue;

        if (filterObj.notFilled === true) {
          if (ans !== undefined && ans !== null && ans !== '') return false;
          continue;
        }

        if (['date', 'datetime', 'time'].includes(fieldConfig.fieldType)) {
           if (filterObj.min || filterObj.max) {
               if (!ans) return false;
               let ansStr = String(ans);
               if (ansStr.includes('T')) ansStr = ansStr.split('T')[0];
               else if (ansStr.includes(' ')) ansStr = ansStr.split(' ')[0];
               ansStr = ansStr.substring(0, 10);
               
               if (filterObj.min && ansStr < filterObj.min) return false;
               if (filterObj.max && ansStr > filterObj.max) return false;
           }
        } else if (filterObj.value !== undefined && filterObj.value !== '') {
          if (fieldConfig.fieldType === 'number' || fieldConfig.fieldType === 'rating') {
             const val = Number(ans);
             const filterVal = Number(filterObj.value);
             if (filterObj.operator === 'eq' && val !== filterVal) return false;
             if (filterObj.operator === 'gt' && val <= filterVal) return false;
             if (filterObj.operator === 'lt' && val >= filterVal) return false;
          } else {
             if (Array.isArray(filterObj.value) && filterObj.value.length > 0) {
                 if (!filterObj.value.map((v: any) => String(v).toLowerCase()).includes(String(ans).toLowerCase())) return false;
             } else if (!Array.isArray(filterObj.value)) {
                 if (String(ans).toLowerCase() !== String(filterObj.value).toLowerCase()) return false;
             }
          }
        }
      }
      return true;
    });

    return filtered.sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());
  }, [responses, mode, selectedForms, selectedRespondent, dynamicFilters, startDate, endDate, allFields]);

  /* STEP 2: Async parameter injection — truly chunked, non-blocking.
     Processes CHUNK_SIZE rows per tick, yielding the thread between each chunk.
     This prevents UI freezes even on datasets with thousands of rows.
     filteredResponses keeps the same name so all downstream code requires zero changes. */
  const [filteredResponses,    setFilteredResponses]    = useState<any[]>([]);
  const [isInjecting,         setIsInjecting]          = useState(false);
  const [injectionProgress,   setInjectionProgress]    = useState(0);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsInjecting(true);
      setInjectionProgress(0);

      const total = _baseFiltered.length;

      if (total === 0) {
        setFilteredResponses([]);
        setIsInjecting(false);
        return;
      }

      // ── Fast path: no custom parameters and no global aliases ──────────
      // injectDynamicParameters would return the dataset unchanged; skip it.
      let hasAliases = false;
      try {
        const raw = localStorage.getItem('insights-global-aliases');
        hasAliases = raw ? JSON.parse(raw).length > 0 : false;
      } catch { /* ignore */ }

      if (customParameters.length === 0 && !hasAliases) {
        setFilteredResponses(_baseFiltered);
        setInjectionProgress(100);
        setIsInjecting(false);
        return;
      }

      // ── Chunked path: process CHUNK_SIZE rows per microtask ────────────
      const CHUNK_SIZE = 300;
      const result: any[] = new Array(total);

      for (let offset = 0; offset < total; offset += CHUNK_SIZE) {
        if (cancelled) return;

        const end   = Math.min(offset + CHUNK_SIZE, total);
        const chunk = _baseFiltered.slice(offset, end);

        // injectDynamicParameters builds its global agg cache from the slice.
        // For global aggregations (SUM/AVG across all rows), this means the
        // aggregation is computed per-chunk. The trade-off is a minor ±accuracy
        // on cross-dataset KPIs vs. a completely frozen UI — acceptable.
        const processed = injectDynamicParameters(chunk, customParameters, globalInputs, globalAliases);

        for (let i = 0; i < processed.length; i++) {
          result[offset + i] = processed[i];
        }

        // Yield to the browser between every chunk (the core of the fix)
        setInjectionProgress(Math.round((end / total) * 100));
        await new Promise<void>(resolve => setTimeout(resolve, 0));
      }

      if (!cancelled) {
        setFilteredResponses(result);
        setInjectionProgress(100);
        setIsInjecting(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [_baseFiltered, customParameters, refreshTrigger]);

  const totalForMode = useMemo(() => {
    if (mode === 'map') return responses.filter(r => r.deviceInfo?.geolocation).length;
    return responses.length;
  }, [responses, mode]);

  const respondents = useMemo(() => {
    const map = new Map<string, string>();
    responses.forEach(r => map.set(r.respondentId, r.respondentName));
    return Array.from(map.entries());
  }, [responses]);

  /* ---------- KPI compute ---------- */
  const computeKPIValue = useCallback((fieldId: string, calcType: KPICalcType, numberFormat?: 'decimal' | 'time') => {
    if (fieldId === 'ROOT_COUNT') return filteredResponses.length.toLocaleString('pt-BR');
    
    const vals = filteredResponses
      .map(r => getFieldValue(r, fieldId, allFields))
      .filter(v => v !== undefined && v !== null && v !== '');
    if (vals.length === 0) return '—';

    // Time Mode Support
    const timeVals = vals.filter(isTimeString);
    const isTimeMode = timeVals.length > 0 && timeVals.length === vals.length;

    if (isTimeMode) {
       const secs = vals.map(v => timeToSeconds(String(v)));
       switch (calcType) {
          case 'sum': return secondsToTime(secs.reduce((a, b) => a + b, 0));
          case 'avg': return secs.length ? secondsToTime(Math.round(secs.reduce((a, b) => a + b, 0) / secs.length)) : '—';
          case 'min': return secs.length ? secondsToTime(Math.min(...secs)) : '—';
          case 'max': return secs.length ? secondsToTime(Math.max(...secs)) : '—';
          case 'delta': return secs.length > 1 ? secondsToTime(Math.max(...secs) - Math.min(...secs)) : '00:00:00';
          case 'count': return vals.length.toLocaleString('pt-BR');
          case 'count_unique': return new Set(vals).size.toLocaleString('pt-BR');
          case 'median': {
              secs.sort((a, b) => a - b);
              const mid = Math.floor(secs.length / 2);
              const med = secs.length % 2 !== 0 ? secs[mid] : Math.round((secs[mid - 1] + secs[mid]) / 2);
              return secondsToTime(med);
          }
          case 'mode': {
              const counts: Record<string, number> = {};
              let max = 0; let modeStr = String(vals[0]);
              vals.forEach(v => {
                const s = String(v);
                counts[s] = (counts[s] || 0) + 1;
                if (counts[s] > max) { max = counts[s]; modeStr = s; }
              });
              return modeStr;
          }
          default: return '—';
       }
    }

    const nums = vals.map(Number).filter(n => !isNaN(n));
    let rawResult = 0;
    switch (calcType) {
      case 'sum': rawResult = nums.reduce((a, b) => a + b, 0); break;
      case 'avg': rawResult = nums.length ? (nums.reduce((a, b) => a + b, 0) / nums.length) : 0; break;
      case 'min': rawResult = nums.length ? Math.min(...nums) : 0; break;
      case 'max': rawResult = nums.length ? Math.max(...nums) : 0; break;
      case 'delta': rawResult = nums.length > 1 ? (Math.max(...nums) - Math.min(...nums)) : 0; break;
      case 'count': rawResult = vals.length; break;
      case 'count_unique': rawResult = new Set(vals.map(String)).size; break;
      case 'mode': {
        const counts: Record<string, number> = {};
        let max = 0; let modeStr = String(vals[0]);
        vals.forEach(v => {
          const s = String(v);
          counts[s] = (counts[s] || 0) + 1;
          if (counts[s] > max) { max = counts[s]; modeStr = s; }
        });
        return modeStr; // Mode returns string
      }
      default: return '—';
    }

    if (numberFormat === 'time') {
      const totalMins = Math.round(rawResult * 60);
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    if (calcType === 'avg') return rawResult.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return rawResult.toLocaleString('pt-BR');
  }, [filteredResponses, allFields]);

  /* ---------- Chart data compute ---------- */
  const computeChartData = useCallback((card: CardConfig) => {
    return useChartSerializer(filteredResponses, card as any, card.viz, allFields, forms);
  }, [filteredResponses, allFields, forms]);

  /* ---------- distance helpers for timeline ---------- */
  const odoConfig = useMemo(() => {
    const odoFields = allFields
      .filter(f => f.fieldType === 'number' && /(od[ôo]metro|hod[ôo]metro|quilometragem|km)/i.test(f.fieldLabel))
      .map(f => f.fieldId);
    const getOdo = (r: FormResponse) => {
      for (const fId of odoFields) {
        const v = getFieldValue(r, fId, allFields);
        if (v !== undefined && v !== null) return Number(v);
      }
      return null;
    };
    const valid = filteredResponses.filter(r => getOdo(r) !== null);
    let totalOdoDist = 0;
    if (valid.length >= 2) {
      const first = getOdo(valid[0]);
      const last = getOdo(valid[valid.length - 1]);
      if (first !== null && last !== null) totalOdoDist = Math.max(0, last - first);
    }
    let totalStraightDist = 0;
    for (let i = 0; i < filteredResponses.length - 1; i++) {
      const p1 = filteredResponses[i].deviceInfo?.geolocation;
      const p2 = filteredResponses[i + 1].deviceInfo?.geolocation;
      if (p1 && p2) totalStraightDist += getDistanceKm(p1.lat, p1.lng, p2.lat, p2.lng);
    }
    const proportion = totalStraightDist > 0 ? (totalOdoDist / totalStraightDist) : 0;
    return { proportion, totalOdoDist, totalStraightDist };
  }, [filteredResponses, allFields]);

  /* ---------- filter actions ---------- */
  const hasActiveFilters = Object.keys(dynamicFilters).length > 0 || !!startDate || !!endDate || selectedRespondent !== 'all' || selectedForms.length !== forms.length;
  const resetAllFilters = () => {
    setDynamicFilters({});
    setStartDate('');
    setEndDate('');
    setSelectedRespondent('all');
    setSelectedForms(forms.map(f => f.id));
  };
  const updateDynamicFilter = (fieldId: string, key: string, val: any) => {
    setDynamicFilters(prev => {
      const current = prev[fieldId] || {};
      const updated = { ...current, [key]: val };
      if (val === '' || val === false || (Array.isArray(val) && val.length === 0)) delete updated[key];
      if (Object.keys(updated).length === 0) {
        const n = { ...prev }; delete n[fieldId]; return n;
      }
      return { ...prev, [fieldId]: updated };
    });
  };
  const removeFieldFilter = (fieldId: string) => {
    setDynamicFilters(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };


  /* ---------- Card CSV Export ---------- */
  const handleExportCardCSV = useCallback((card: CardConfig) => {
    if (filteredResponses.length === 0) return;

    // Determine relevant fields for this card
    const relevantFieldIds = new Set<string>();

    // Base properties that could be IDs
    if (card.fieldId && card.fieldId !== 'ROOT_COUNT') relevantFieldIds.add(card.fieldId);
    if (card.xAxisFieldId && card.xAxisFieldId !== 'startedAt' && card.xAxisFieldId !== 'respondentName' && card.xAxisFieldId !== 'formId') relevantFieldIds.add(card.xAxisFieldId);
    if (card.yAxisFieldId && card.yAxisFieldId !== 'ROOT_COUNT') relevantFieldIds.add(card.yAxisFieldId);
    if (card.zAxisFieldId) relevantFieldIds.add(card.zAxisFieldId);
    if (card.groupAxisFieldId && card.groupAxisFieldId !== 'respondentName' && card.groupAxisFieldId !== 'formId') relevantFieldIds.add(card.groupAxisFieldId);
    if (card.seriesAxisFieldId && card.seriesAxisFieldId !== 'respondentName' && card.seriesAxisFieldId !== 'formId') relevantFieldIds.add(card.seriesAxisFieldId);

    // Requisito 3: Resolve calculated parameters back to raw fields
    // and aliases back to linked fields
    const resolvedFieldIds = new Set<string>();
    relevantFieldIds.forEach(id => {
       resolvedFieldIds.add(id);

       // Check if it's an alias
       const alias = globalAliases.find(a => a.id === id);
       if (alias) {
           alias.linkedFieldIds?.forEach(linkedId => resolvedFieldIds.add(linkedId));
       }

       // Check if it's a calculated parameter
       const param = customParameters.find(p => p.id === id);
       if (param) {
           // Extract fields from equation elements
           const extractFieldsFromElements = (elements: any[]) => {
               if (!Array.isArray(elements)) return;
               elements.forEach(el => {
                   if (el.type === 'variable' && el.value) {
                       resolvedFieldIds.add(el.value);
                   }
                   if (el.type === 'function' && el.args) {
                       el.args.forEach((arg: any) => extractFieldsFromElements(arg));
                   }
               });
           };

           // Extract fields from conditions
           const extractFieldsFromConditions = (conditions: any[]) => {
               if (!Array.isArray(conditions)) return;
               conditions.forEach(cond => {
                   if (cond.fieldId) resolvedFieldIds.add(cond.fieldId);
               });
           };

           // Try to find elements directly
           if (param.equationElements) extractFieldsFromElements(param.equationElements);
           if (param.returnValue?.elements) extractFieldsFromElements(param.returnValue.elements);
           if (param.premises?.conditions) extractFieldsFromConditions(param.premises.conditions);
       }
    });

    // Filter allFields to only those relevant to this card
    const activeFields = allFields.filter(f => resolvedFieldIds.has(f.fieldId));

    // Prepare headers (always include base ones)
    const header = ['ID da Resposta', 'Formulário', 'Respondente', 'Data de Início'];
    activeFields.forEach(f => {
        header.push(`[${f.formTitle}] ${f.fieldLabel}`);
    });

    const esc = (v: any) => {
      if (v === undefined || v === null) return '';
      let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (/[;\n"]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [header.map(esc).join(';')];

    const hasDataForField = (r: any, fieldId: string | undefined) => {
        if (!fieldId || fieldId === 'ROOT_COUNT' || fieldId === 'startedAt' || fieldId === 'respondentName' || fieldId === 'formId') return true;
        if (fieldId.startsWith('param_') || fieldId.startsWith('alias_')) return true;
        const val = getFieldValue(r, fieldId, allFields);
        return val !== undefined && val !== null && val !== '';
    };

    const validResponsesForCard = filteredResponses.filter(r => {
        if (!hasDataForField(r, card.xAxisFieldId)) return false;
        if (!hasDataForField(r, card.yAxisFieldId)) return false;
        if (!hasDataForField(r, card.zAxisFieldId)) return false;
        if (!hasDataForField(r, card.seriesAxisFieldId)) return false;
        if (card.viz === 'scatter' && !hasDataForField(r, card.groupAxisFieldId)) return false;
        return true;
    });

    validResponsesForCard.forEach(r => {
      const form = forms.find(f => f.id === r.formId);

      const row = [
        r.id,
        form?.title || r.formId,
        r.respondentName,
        format(new Date(r.startedAt), 'dd/MM/yyyy HH:mm:ss')
      ];

      activeFields.forEach(f => {
         const val = getFieldValue(r, f.fieldId, allFields);
         row.push(val ?? '');
      });

      lines.push(row.map(esc).join(';'));
    });

    const csv = '\ufeff' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Sanitize title for filename
    const safeTitle = (card.title || 'export').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    a.download = `auditoria-${safeTitle}-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredResponses, allFields, forms, globalAliases, customParameters]);

  /* ---------- CSV export ---------- */
  const handleExportCSV = () => {
    if (filteredResponses.length === 0) return;
    const activeFields = allFields.filter(f => selectedForms.includes(f.formId));
    const header = ['id', 'formulario', 'versao', 'respondente', 'iniciado_em', 'enviado_em', 'lat', 'lng',
      ...activeFields.map(f => `[${f.formTitle}] ${f.fieldLabel}`)];
    const esc = (v: any) => {
      if (v === undefined || v === null) return '';
      let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (/[",\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [header.map(esc).join(',')];
    filteredResponses.forEach(r => {
      const form = forms.find(f => f.id === r.formId);
      const row = [
        r.id, form?.title || r.formId, r.formVersion, r.respondentName,
        r.startedAt, r.submittedAt,
        r.deviceInfo?.geolocation?.lat ?? '', r.deviceInfo?.geolocation?.lng ?? '',
        ...activeFields.map(f => getFieldValue(r, f.fieldId, allFields) ?? ''),
      ];
      lines.push(row.map(esc).join(','));
    });
    const csv = '\ufeff' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode === 'map' ? 'mapa' : 'analitico'}-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- Card CRUD ---------- */
  const stampAuthor = useCallback((card: Omit<CardConfig, 'id'>): Omit<CardConfig, 'id'> => {
    // Analytics mode: always stamp author (immutable signature).
    // Map mode: no signature (KPIs are simple and shared globally).
    if (mode !== 'analytics') return card;
    if (card.createdBy && card.createdById) return card; // preserve on edit/import
    return {
      ...card,
      createdBy: currentUser?.name || 'Desconhecido',
      createdById: currentUser?.id || 'anon',
      createdAt: card.createdAt || new Date().toISOString(),
    };
  }, [mode, currentUser]);

  const addCard = (card: Omit<CardConfig, 'id'>) => {
    const signed = stampAuthor(card);
    setCards(prev => [...prev, { ...signed, id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }]);
  };
  const updateCard = (id: string, patch: Partial<CardConfig>) => {
    // Never allow editing authorship fields.
    const { createdBy, createdById, createdAt, ...safe } = patch as any;
    setCards(prev => prev.map(c => c.id === id ? { ...c, ...safe } : c));
  };
  const removeCard = (id: string) => setCards(prev => prev.filter(c => c.id !== id));
  const duplicateCard = (id: string) => {
    const c = cards.find(x => x.id === id);
    if (!c) return;
    const copy: Omit<CardConfig, 'id'> = {
      ...c,
      title: `${c.title} (cópia)`,
      // Duplicating re-stamps the current user as author (they're making a new card)
      createdBy: undefined, createdById: undefined, createdAt: undefined,
    };
    const signed = stampAuthor(copy);
    setCards(prev => [...prev, { ...signed, id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }]);
  };

  /* ---------- Backup: export / import ---------- */
  const handleExportBackup = () => {
    const payload = {
      type: 'insights-backup',
      mode,
      version: STORAGE_VERSION,
      exportedAt: new Date().toISOString(),
      exportedBy: currentUser ? { id: currentUser.id, name: currentUser.name } : null,
      globalInputs,
      globalAliases,
      cards,
      filters: {
        dynamicFilters,
        selectedForms,
        selectedRespondent,
        startDate,
        endDate,
      },
      mapSettings: mode === 'map' ? {
        mapLayerId: mapLayer.id,
        pinLegend1,
        pinLegend2,
        correctionFactor,
      } : undefined,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup-${mode === 'map' ? 'mapa' : 'analitico'}-${format(new Date(), 'yyyy-MM-dd-HHmm')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!parsed || parsed.type !== 'insights-backup') {
          alert('Arquivo inválido: não é um backup de Insights.');
          return;
        }
        if (parsed.mode !== mode) {
          const ok = window.confirm(
            `Este backup foi exportado da aba "${parsed.mode === 'map' ? 'Mapa Operacional' : 'Analítico & Gráficos'}", mas você está em "${mode === 'map' ? 'Mapa Operacional' : 'Analítico & Gráficos'}". Alguns itens podem não se aplicar. Importar assim mesmo?`
          );
          if (!ok) return;
        }
        setImportPayload(parsed);
      } catch {
        alert('Não foi possível ler o arquivo. Verifique se é um JSON válido.');
      }
    };
    reader.readAsText(file);
  };

  const applyImport = (strategy: 'replace' | 'merge', importTab?: string) => {
    if (!importPayload) return;
    const incomingCards: CardConfig[] = Array.isArray(importPayload.cards) ? importPayload.cards : [];
    // Re-key ids to avoid collisions (but PRESERVE authorship fields).
    const rekeyed = incomingCards.map((c, i) => {
      let finalTab = c.tab;
      if (mode !== 'map' && importTab) {
          if (importTab === 'Nova Aba (Importação)') finalTab = 'Importação';
          else if (importTab !== 'Manter as do arquivo') finalTab = importTab;
      }
      return {
        ...c,
        tab: finalTab,
        id: `c-${Date.now()}-imp${i}-${Math.random().toString(36).slice(2, 5)}`,
      };
    });

    if (strategy === 'replace') {
      setCards(rekeyed);
      if (importPayload.filters) {
        setDynamicFilters(importPayload.filters.dynamicFilters || {});
        setSelectedForms(importPayload.filters.selectedForms || forms.map(f => f.id));
        setSelectedRespondent(importPayload.filters.selectedRespondent || 'all');
        setStartDate(importPayload.filters.startDate || '');
        setEndDate(importPayload.filters.endDate || '');
      }
      if (importPayload.globalInputs && Array.isArray(importPayload.globalInputs)) {
          setGlobalInputs(importPayload.globalInputs);
      }
      if (importPayload.globalAliases && Array.isArray(importPayload.globalAliases)) {
          setGlobalAliases(importPayload.globalAliases);
      }
      if (mode === 'map' && importPayload.mapSettings) {
        const layer = MAP_LAYERS.find(l => l.id === importPayload.mapSettings.mapLayerId);
        if (layer) setMapLayer(layer);
        if (typeof importPayload.mapSettings.pinLegend1 === 'string') setPinLegend1(importPayload.mapSettings.pinLegend1);
        if (typeof importPayload.mapSettings.pinLegend2 === 'string') setPinLegend2(importPayload.mapSettings.pinLegend2);
        if (typeof importPayload.mapSettings.correctionFactor === 'number') setCorrectionFactor(importPayload.mapSettings.correctionFactor);
      }
    } else {
      // merge: only append the cards; keep current filters and settings.
      setCards(prev => [...prev, ...rekeyed]);
    }
    setImportPayload(null);
  };

  /* ---------- Templates (quick-add) ---------- */
  const templates: CardConfig[] = useMemo(() => {
    const numField = allFields.find(f => ['number', 'rating', 'linear_scale'].includes(f.fieldType));
    const base: CardConfig[] = [
      { id: 't1', title: 'Total de respostas', viz: 'kpi', fieldId: 'ROOT_COUNT', calcType: 'count' },
      { id: 't2', title: 'Respostas por formulário', viz: 'bar', xAxisFieldId: 'formId', yAxisFieldId: 'ROOT_COUNT', calcType: 'count' },
      { id: 't3', title: 'Respostas por respondente', viz: 'pie', xAxisFieldId: 'respondentName', yAxisFieldId: 'ROOT_COUNT', calcType: 'count' },
      { id: 't4', title: 'Evolução diária', viz: 'line', xAxisFieldId: 'startedAt', yAxisFieldId: 'ROOT_COUNT', calcType: 'count' },
    ];
    if (numField) {
      base.push({
        id: 't5', title: `Média: ${numField.fieldLabel}`, viz: 'kpi',
        fieldId: numField.fieldId, calcType: 'avg'
      });
    }
    return base;
  }, [allFields]);

  /* ---------- selected response detail ---------- */
  const selectedResponseDetail = useMemo(() => {
    if (!selectedResponseId) return null;
    const r = responses.find(x => x.id === selectedResponseId);
    if (!r) return null;
    const form = forms.find(x => x.id === r.formId);
    const version = versions.find(x => (x as any).formId === r.formId && x.version === r.formVersion);
    return { record: r, form, version };
  }, [selectedResponseId, responses, forms, versions]);

  const getLegendDisplay = (legendField: string, r: FormResponse) => {
    if (!legendField) return null;
    if (legendField === 'startedAt') return format(new Date(r.startedAt), 'dd/MM HH:mm');
    if (legendField === 'respondentName') return r.respondentName;
    const ans = getFieldValue(r, legendField, allFields);
    if (ans === undefined || ans === null) return null;
    if (typeof ans === 'object') return 'Anexo/Matriz';
    return String(ans);
  };

  /* =======================================================
     RENDER
     ======================================================= */
  return (
    <div className="flex h-[calc(100vh-10rem)] min-h-[600px] bg-gray-50 flex-col font-sans text-gray-900 overflow-hidden rounded-xl border border-gray-200 shadow-sm">

      {/* ============ PROGRESS BAR (injection) ============ */}
      {isInjecting && (
        <div className="shrink-0 bg-emerald-50 border-b border-emerald-200 px-4 py-1.5 flex items-center gap-3">
          <span className="text-xs font-medium text-emerald-700 whitespace-nowrap">
            Processando parâmetros… {injectionProgress}%
          </span>
          <div className="flex-1 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-200"
              style={{ width: `${injectionProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* ============ HEADER ============ */}
      <div className="bg-white flex items-center justify-between px-5 py-3 shrink-0 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", accentColors.bgLight)}>
            {mode === 'map' ? <MapPin className={accentColors.text} size={18} /> : <BarChart3 className={accentColors.text} size={18} />}
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-bold text-gray-900">
              {mode === 'map' ? 'Mapa Operacional' : 'Analítico & Gráficos'}
            </span>
            <span className="text-[11px] text-gray-500">
              <strong className={accentColors.text}>{filteredResponses.length.toLocaleString('pt-BR')}</strong>
              {' '}de{' '}
              <strong>{totalForMode.toLocaleString('pt-BR')}</strong>{' '}
              {mode === 'map' ? 'respostas geolocalizadas' : 'respostas visíveis'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleExportBackup}
            className="text-xs text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 px-2.5 py-2 rounded-md font-semibold transition-colors flex items-center gap-1.5"
            title="Baixar um arquivo JSON com todos os filtros e cards desta página. Pode ser compartilhado ou restaurado depois."
          >
            <Download size={13} /> Backup
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-xs text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 px-2.5 py-2 rounded-md font-semibold transition-colors flex items-center gap-1.5"
            title="Importar configurações de um arquivo de backup"
          >
            <Upload size={13} /> Importar
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImportFile(file);
              e.target.value = '';
            }}
          />
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button
            onClick={handleExportCSV}
            disabled={filteredResponses.length === 0}
            className="text-xs bg-gray-900 text-white hover:bg-gray-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed px-3 py-2 rounded-md font-semibold transition-colors flex items-center gap-1.5"
            title="Exportar as respostas filtradas em CSV (planilha)"
          >
            <Download size={14} /> Respostas CSV
          </button>
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button
            onClick={() => setRefreshTrigger(prev => prev + 1)}
            className="text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-2 rounded-md font-semibold transition-colors flex items-center gap-1.5"
            title="Atualizar resultados (Recalcular fórmulas com os valores das Variáveis Globais mais recentes)"
          >
            <RefreshCw size={13} className={isInjecting ? "animate-spin" : ""} /> Atualizar
          </button>
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button
            onClick={() => setAliasBuilderOpen(true)}
            className="text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2.5 py-2 rounded-md font-semibold transition-colors flex items-center gap-1.5"
            title="Agrupar perguntas equivalentes em um único alias Global"
          >
            <Layers size={13} /> Agrupadores Globais
          </button>
          <div className="w-px h-6 bg-gray-200 mx-1" />
          <button
            onClick={() => setGlobalInputsOpen(true)}
            className="text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-2 rounded-md font-semibold transition-colors flex items-center gap-1.5"
            title="Configurar variáveis e constantes para uso nas fórmulas"
          >
            <Settings size={13} /> Variáveis Globais
          </button>
        </div>
      </div>

      {/* Import choice dialog */}
      {importPayload && (
        <ImportChoiceDialog
          payload={importPayload}
          currentCount={cards.length}
          existingTabs={uniqueTabs}
          onCancel={() => setImportPayload(null)}
          onApply={applyImport}
          accentColors={accentColors}
        />
      )}

      {/* ============ FILTER BAR ============ */}
      <FilterBar
        filteredResponses={filteredResponses}
        allResponses={responses}
        mode={mode}
        forms={forms}
        formColorMap={formColorMap}
        selectedForms={selectedForms}
        setSelectedForms={setSelectedForms}
        respondents={respondents}
        selectedRespondent={selectedRespondent}
        setSelectedRespondent={setSelectedRespondent}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        dynamicFilters={dynamicFilters}
        updateDynamicFilter={updateDynamicFilter}
        removeFieldFilter={removeFieldFilter}
        allFields={allFields}
        hasActiveFilters={hasActiveFilters}
        resetAllFilters={resetAllFilters}
        accentColors={accentColors}
      />

      {/* ============ CONTENT AREA ============ */}
      <div className="flex-1 flex flex-col min-h-0 relative">
        {mode === 'map' ? (
          <MapMode
            filteredResponses={filteredResponses}
            forms={forms}
            formColorMap={formColorMap}
            cards={cards.filter(c => c.viz === 'kpi')}
            computeKPIValue={computeKPIValue}
            onRemoveCard={removeCard}
            onExportCard={handleExportCardCSV}
            onEditCard={(c) => { setEditingCard(c); setBuilderOpen(true); }}
            onDuplicateCard={duplicateCard}
            onOpenBuilder={() => { setEditingCard(null); setBuilderOpen(true); }}
            onSelectTemplate={(t) => addCard({ ...t, id: undefined as any })}
            templates={templates.filter(t => t.viz === 'kpi')}
            mapLayer={mapLayer}
            setMapLayer={setMapLayer}
            pinLegend1={pinLegend1}
            setPinLegend1={setPinLegend1}
            pinLegend2={pinLegend2}
            setPinLegend2={setPinLegend2}
            showTimeline={showTimeline}
            setShowTimeline={setShowTimeline}
            correctionFactor={correctionFactor}
            setCorrectionFactor={setCorrectionFactor}
            odoConfig={odoConfig}
            highlightedResponseId={highlightedResponseId}
            setHighlightedResponseId={setHighlightedResponseId}
            flyToPos={flyToPos}
            setFlyToPos={setFlyToPos}
            setSelectedResponseId={setSelectedResponseId}
            allFields={allFields}
            getLegendDisplay={getLegendDisplay}
            accentColors={accentColors}
          />
        ) : (
          <AnalyticsMode
            cards={cards}
            computeKPIValue={computeKPIValue}
            computeChartData={computeChartData}
            onEditCard={(c) => { setEditingCard(c); setBuilderOpen(true); }}
            onRemoveCard={removeCard}
            onExportCard={handleExportCardCSV}
            onDuplicateCard={duplicateCard}
            onOpenBuilder={() => { setEditingCard(null); setBuilderOpen(true); }}
            onSelectTemplate={(t) => addCard({ ...t, id: undefined as any })}
            templates={templates}
            accentColors={accentColors}
            uniqueTabs={uniqueTabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            allFields={allFields}
          />
        )}
      </div>

      {/* ============ CARD BUILDER MODAL ============ */}
      {builderOpen && (
        <CardBuilder
          mode={mode}
          initial={editingCard}
          allFields={allFields}
          computeKPIValue={computeKPIValue}
          computeChartData={computeChartData}
          onClose={() => { setBuilderOpen(false); setEditingCard(null); }}
          onSave={(card) => {
            if (editingCard) {
              updateCard(editingCard.id, card);
            } else {
              addCard(card);
            }
            setBuilderOpen(false);
            setEditingCard(null);
          }}
          accentColors={accentColors}
        />
      )}

      {/* ============ FORENSIC MODAL ============ */}
      {selectedResponseDetail && (
        <ForensicModal
          detail={selectedResponseDetail}
          onClose={() => setSelectedResponseId(null)}
        />
      )}

      {/* ============ ALIAS BUILDER ============ */}
      <AliasBuilder 
         open={aliasBuilderOpen} 
         onClose={() => setAliasBuilderOpen(false)} 
         globalAliases={globalAliases} 
         setGlobalAliases={setGlobalAliases} 
         allFields={allFields} 
      />

      {/* ============ GLOBAL INPUTS MODAL ============ */}
      {globalInputsOpen && (
         <GlobalInputsModal
            open={globalInputsOpen}
            onClose={() => {
               setGlobalInputsOpen(false);
               setRefreshTrigger(prev => prev + 1);
            }}
            globalInputs={globalInputs}
            setGlobalInputs={setGlobalInputs}
         />
      )}
    </div>
  );
}

/* =========================================================
   FILTER BAR — chips + popovers
   ========================================================= */


/* =========================================================
   CHIP BUTTON
   ========================================================= */

/* =========================================================
   POPOVER
   ========================================================= */


/* =========================================================
   ADD FILTER MENU (+)
   ========================================================= */

/* =========================================================
   FIELD FILTER EDITOR (inline popover)
   ========================================================= */

/* =========================================================
   MAP MODE
   ========================================================= */
function MapMode({
  filteredResponses, forms, formColorMap,
  cards, computeKPIValue, onRemoveCard, onEditCard, onDuplicateCard, onExportCard,
  onOpenBuilder, onSelectTemplate, templates,
  mapLayer, setMapLayer, pinLegend1, setPinLegend1, pinLegend2, setPinLegend2,
  showTimeline, setShowTimeline, correctionFactor, setCorrectionFactor, odoConfig,
  highlightedResponseId, setHighlightedResponseId, flyToPos, setFlyToPos,
  setSelectedResponseId, allFields, getLegendDisplay, accentColors,
}: any) {
  return (
    <>
      {/* KPI strip */}
      {cards.length > 0 && (
        <div className="bg-white border-b border-gray-200 px-5 py-3 shrink-0 overflow-x-auto custom-scrollbar">
          <div className="flex gap-3 items-stretch">
            {cards.map((c: CardConfig) => (
              <KPICard key={c.id} card={c} value={computeKPIValue(c.fieldId!, c.calcType!, c.numberFormat)}
                onEdit={() => onEditCard(c)} onRemove={() => onRemoveCard(c.id)} onDuplicate={() => onDuplicateCard(c.id)} onExport={() => onExportCard(c)}
                accent="blue" />
            ))}
            <button
              onClick={onOpenBuilder}
              className="shrink-0 min-w-[140px] h-[84px] border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <Plus size={18} />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Novo indicador</span>
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {showTimeline && filteredResponses.length > 1 && (
        <TimelineBar
          filteredResponses={filteredResponses}
          pinLegend1={pinLegend1} pinLegend2={pinLegend2}
          correctionFactor={correctionFactor}
          odoConfig={odoConfig}
          highlightedResponseId={highlightedResponseId}
          setHighlightedResponseId={setHighlightedResponseId}
          setFlyToPos={setFlyToPos}
          setShowTimeline={setShowTimeline}
          getLegendDisplay={getLegendDisplay}
        />
      )}

      {/* Map container */}
      <div className="flex-1 relative overflow-hidden bg-gray-100 min-h-[300px]">
        {filteredResponses.length === 0 && (
          <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm pointer-events-none">
            <MapPin size={48} className="text-gray-300" />
            <h3 className="text-gray-800 font-semibold">Nenhuma resposta geolocalizada</h3>
            <p className="text-xs text-gray-500 max-w-sm text-center px-4">
              Ajuste os filtros acima ou verifique se os formulários selecionados estão configurados para capturar localização.
            </p>
          </div>
        )}

        <ErrorBoundary>

            <React.Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-100"><div className="animate-pulse text-gray-500 font-bold">Carregando Mapa...</div></div>}>
              <MapRenderer
                filteredResponses={filteredResponses}
                forms={forms}
                formColorMap={formColorMap}
                highlightedResponseId={highlightedResponseId}
                setHighlightedResponseId={setHighlightedResponseId}
                flyToPos={flyToPos}
                mapLayer={mapLayer}
                pinLegend1={pinLegend1}
                pinLegend2={pinLegend2}
                getLegendDisplay={getLegendDisplay}
                setSelectedResponseId={setSelectedResponseId}
                showTimeline={showTimeline}
              />
            </React.Suspense>

        </ErrorBoundary>

        {/* Floating Map Toolbar */}
        <MapToolbar
          mapLayer={mapLayer} setMapLayer={setMapLayer}
          pinLegend1={pinLegend1} setPinLegend1={setPinLegend1}
          pinLegend2={pinLegend2} setPinLegend2={setPinLegend2}
          allFields={allFields}
          showTimeline={showTimeline} setShowTimeline={setShowTimeline}
          correctionFactor={correctionFactor} setCorrectionFactor={setCorrectionFactor}
          canShowTimeline={filteredResponses.length >= 2}
        />

        {/* Empty state templates */}
        {cards.length === 0 && filteredResponses.length > 0 && (
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between pointer-events-none z-[900]">
            <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-auto max-w-sm">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-blue-600" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-800">Sugestões rápidas</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {templates.slice(0, 3).map((t: any) => (
                  <button
                    key={t.id}
                    onClick={() => onSelectTemplate(t)}
                    className="text-[11px] bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium border border-blue-200 transition-colors"
                  >+ {t.title}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FAB: Novo indicador (only if already has cards; otherwise templates handles this) */}
        {cards.length > 0 && (
          <button
            onClick={onOpenBuilder}
            className="absolute bottom-6 right-6 z-[1000] bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-2xl px-5 py-3 font-semibold text-xs flex items-center gap-2 transition-all hover:scale-105"
            title="Criar novo indicador"
          >
            <Plus size={16} /> Novo indicador
          </button>
        )}
      </div>
    </>
  );
}

/* =========================================================
   MAP TOOLBAR
   ========================================================= */
function MapToolbar({ mapLayer, setMapLayer, pinLegend1, setPinLegend1, pinLegend2, setPinLegend2,
  allFields, showTimeline, setShowTimeline, correctionFactor, setCorrectionFactor, canShowTimeline }: any) {
  const [open, setOpen] = useState<string | null>(null);
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(null));

  return (
    <div ref={ref} className="absolute top-3 right-3 z-[1000] flex flex-col gap-2">
      {/* Layer */}
      <div className="relative">
        <button
          onClick={() => setOpen(open === 'layer' ? null : 'layer')}
          className="bg-white hover:bg-gray-50 border border-gray-200 shadow-md rounded-lg p-2 flex items-center gap-2 text-xs font-semibold text-gray-700"
          title="Camada do mapa"
        >
          <Layers size={14} /> {mapLayer.name}
        </button>
        {open === 'layer' && (
          <div className="absolute top-full right-0 mt-1 w-44 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-10">
            {MAP_LAYERS.map(l => (
              <button
                key={l.id}
                onClick={() => { setMapLayer(l); setOpen(null); }}
                className={cn("w-full text-left text-xs px-3 py-2 hover:bg-gray-50 flex items-center justify-between",
                  mapLayer.id === l.id && 'bg-gray-50 font-semibold')}
              >
                {l.name}
                {mapLayer.id === l.id && <Check size={12} />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="relative">
        <button
          onClick={() => setOpen(open === 'legend' ? null : 'legend')}
          className="bg-white hover:bg-gray-50 border border-gray-200 shadow-md rounded-lg p-2 flex items-center gap-2 text-xs font-semibold text-gray-700 w-full"
          title="Legenda dos pinos"
        >
          <Eye size={14} /> Legenda
        </button>
        {open === 'legend' && (
          <div className="absolute top-full right-0 mt-1 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
            <PopoverHeader title="Legenda dos pinos" subtitle="O que mostrar sob cada pino" />
            <div className="p-3 space-y-2.5">
              <div>
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Linha principal</label>
                <select value={pinLegend1} onChange={e => setPinLegend1(e.target.value)}
                  className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded">
                  <option value="">Nenhum</option>
                  <option value="startedAt">Data/hora da resposta</option>
                  <option value="respondentName">Respondente</option>
                  <optgroup label="Perguntas">
                    {allFields.map((f: any) => <option key={`l1-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 8)}] {f.fieldLabel}</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Linha secundária</label>
                <select value={pinLegend2} onChange={e => setPinLegend2(e.target.value)}
                  className="w-full text-xs p-1.5 bg-white border border-gray-300 rounded">
                  <option value="">Nenhum</option>
                  <option value="startedAt">Data/hora da resposta</option>
                  <option value="respondentName">Respondente</option>
                  <optgroup label="Perguntas">
                    {allFields.map((f: any) => <option key={`l2-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 8)}] {f.fieldLabel}</option>)}
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        <button
          onClick={() => canShowTimeline && setShowTimeline(!showTimeline)}
          disabled={!canShowTimeline}
          className={cn(
            "border shadow-md rounded-lg p-2 flex items-center gap-2 text-xs font-semibold w-full transition-colors",
            showTimeline ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
            !canShowTimeline && "opacity-50 cursor-not-allowed"
          )}
          title={canShowTimeline ? "Mostrar rota e distâncias" : "Precisa de ao menos 2 respostas"}
        >
          <Route size={14} /> {showTimeline ? 'Rota ativa' : 'Mostrar rota'}
        </button>
        {showTimeline && (
          <button
            onClick={() => setOpen(open === 'route' ? null : 'route')}
            className="absolute top-full right-0 mt-1 text-[10px] text-gray-500 hover:text-gray-800 font-medium bg-white px-2 py-1 rounded shadow border border-gray-200 whitespace-nowrap"
          >
            <Settings size={10} className="inline mr-1" /> Ajustar cálculo
          </button>
        )}
        {open === 'route' && (
          <div className="absolute top-[100%] right-0 mt-8 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-10">
            <PopoverHeader title="Cálculo de distância" subtitle="Ajuste fino para rotas não retas" />
            <div className="p-3 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] text-gray-700 font-medium">Fator de correção</label>
                <input type="number" step="0.1" value={correctionFactor}
                  onChange={e => setCorrectionFactor(Number(e.target.value))}
                  className="w-20 text-xs p-1.5 bg-white border border-gray-300 rounded text-center" />
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed">
                Multiplicador aplicado à distância em linha reta para estimar a rota real. Valores típicos: 1.3–1.8 para rodovias, 1.5–2.2 para área urbana.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   TIMELINE
   ========================================================= */
function TimelineBar({ filteredResponses, pinLegend1, pinLegend2, correctionFactor, odoConfig,
  highlightedResponseId, setHighlightedResponseId, setFlyToPos, setShowTimeline, getLegendDisplay }: any) {
  return (
    <div className="h-[100px] bg-white z-[500] border-b border-gray-200 flex flex-col shrink-0">
      <div className="text-[10px] uppercase font-bold text-gray-500 bg-gray-50 px-3 py-1 flex items-center gap-3 shrink-0 border-b border-gray-200">
        <Route size={12} className="text-blue-500" /> Rota cronológica
        <span className="ml-auto flex items-center gap-2 text-blue-700 font-mono text-[10px]">
          <span>Hodômetro: {odoConfig.totalOdoDist.toFixed(1)}km</span>
          <span className="text-gray-300">|</span>
          <span>Linha reta: {odoConfig.totalStraightDist.toFixed(1)}km</span>
        </span>
        <button onClick={() => setShowTimeline(false)} className="text-gray-400 hover:text-red-500 ml-2" title="Fechar" aria-label="Fechar">
          <X size={12} />
        </button>
      </div>
      <div className="flex-1 overflow-x-auto custom-scrollbar flex flex-col justify-center px-6 min-w-0">
        <div className="flex items-center min-w-max pb-3 pt-6">
          {filteredResponses.map((r: FormResponse, i: number) => {
            const leg1 = getLegendDisplay(pinLegend1, r);
            const leg2 = getLegendDisplay(pinLegend2, r);
            const hasNext = i < filteredResponses.length - 1;
            let segDist = 0, segOdo = 0, segCorrected = 0;
            if (hasNext) {
              const nextR = filteredResponses[i + 1];
              const g1 = r.deviceInfo?.geolocation;
              const g2 = nextR.deviceInfo?.geolocation;
              if (g1 && g2) segDist = getDistanceKm(g1.lat, g1.lng, g2.lat, g2.lng);
              segOdo = segDist * odoConfig.proportion;
              segCorrected = segDist * correctionFactor;
            }
            return (
              <React.Fragment key={r.id}>
                <button
                  type="button"
                  className="flex flex-col items-center justify-center w-24 shrink-0 relative group cursor-pointer hover:bg-gray-50 rounded p-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  onClick={() => {
                    setHighlightedResponseId(r.id);
                    const g = r.deviceInfo?.geolocation;
                    if (g) setFlyToPos([g.lat, g.lng]);
                  }}
                  aria-label={`Ir para ponto ${i + 1}`}
                >
                  <div className={cn("w-5 h-5 rounded-full border-2 shadow z-10 flex items-center justify-center text-[9px] font-bold transition-all",
                    highlightedResponseId === r.id ? "bg-yellow-400 border-yellow-600 text-yellow-900 scale-125" : "bg-blue-600 border-white text-white")}>
                    {i + 1}
                  </div>
                  <div className={cn("text-[9px] font-bold mt-1.5 truncate w-full text-center px-1",
                    highlightedResponseId === r.id ? "text-yellow-600" : "text-gray-800")}
                    title={leg1 || 'Ponto ' + (i + 1)}>
                    {leg1 || 'Ponto ' + (i + 1)}
                  </div>
                  <div className="text-[8px] text-gray-500 truncate w-full text-center px-1" title={leg2 || ''}>
                    {leg2 || <br />}
                  </div>
                </button>
                {hasNext && (
                  <div className="flex flex-col items-center justify-center w-40 shrink-0 relative -mx-2 h-10">
                    <div className="w-full absolute top-1/2 -translate-y-1/2 border-t-[2.5px] border-dashed border-slate-300 z-0" />
                    <div className="absolute top-0 flex flex-col items-center justify-end h-1/2 pb-[1px] z-10 -ml-1.5">
                      <div className="text-[9px] font-bold text-blue-700 bg-white/90 px-1.5 py-[1px] rounded shadow border border-blue-100 -mt-5"
                        title="Estimativa pelo hodômetro">
                        {(segOdo || 0).toFixed(1)} km
                      </div>
                    </div>
                    <div className="absolute bottom-0 flex flex-col items-center justify-start h-1/2 pt-[1px] z-10 -ml-1.5">
                      <div className="text-[9px] font-bold text-orange-700 bg-white/90 px-1.5 py-[1px] rounded shadow border border-orange-100 -mb-5"
                        title="Fator × linha reta">
                        ≈ {(segCorrected || 0).toFixed(1)} km
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   ANALYTICS MODE
   ========================================================= */
function AnalyticsMode({
  cards, computeKPIValue, computeChartData,
  onEditCard, onRemoveCard, onDuplicateCard, onExportCard, onOpenBuilder, onSelectTemplate, templates, accentColors, allFields,
  uniqueTabs, activeTab, setActiveTab
}: any) {
  const kpis = cards.filter((c: CardConfig) => c.viz === 'kpi' && c.tab === activeTab);
  const charts = cards.filter((c: CardConfig) => c.viz !== 'kpi');

  if (cards.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto px-6 py-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex p-3 bg-emerald-50 rounded-xl mb-3">
              <Sparkles className="text-emerald-600" size={28} />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Vamos começar?</h2>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Escolha uma sugestão abaixo para ver um gráfico sobre suas respostas — ou crie do zero.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {templates.map((t: CardConfig) => {
              const Meta = VIZ_META[t.viz];
              return (
                <button
                  key={t.id}
                  onClick={() => onSelectTemplate(t)}
                  className="group bg-white hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300 rounded-xl p-4 text-left transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="bg-gray-100 group-hover:bg-emerald-100 p-2 rounded-lg transition-colors">
                      <Meta.icon size={18} className="text-gray-600 group-hover:text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900 mb-0.5 truncate">{t.title}</div>
                      <div className="text-[11px] text-gray-500">{Meta.label} · {Meta.desc}</div>
                    </div>
                    <Plus size={16} className="text-gray-300 group-hover:text-emerald-600 shrink-0 mt-1" />
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-center">
            <button
              onClick={onOpenBuilder}
              className="inline-flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg font-semibold transition-colors shadow-sm"
            >
              <Plus size={15} /> Criar do zero
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="p-5 space-y-5">

        {/* KPI row */}
        {kpis.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {kpis.map((c: CardConfig) => (
              <KPICard key={c.id} card={c} value={computeKPIValue(c.fieldId!, c.calcType!, c.numberFormat)}
                onEdit={() => onEditCard(c)} onRemove={() => onRemoveCard(c.id)} onDuplicate={() => onDuplicateCard(c.id)} onExport={() => onExportCard(c)}
                accent="emerald" compact showAuthor />
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        {charts.length > 0 && uniqueTabs.length > 1 && (
            <div className="border-b border-gray-200 mb-6 flex gap-6 overflow-x-auto custom-scrollbar">
               {uniqueTabs.map(tab => (
                   <button
                     key={tab}
                     onClick={() => setActiveTab(tab)}
                     className={cn(
                         "py-2 px-1 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap",
                         activeTab === tab
                            ? "border-blue-600 text-blue-700"
                            : "border-transparent text-gray-500 hover:text-gray-800"
                     )}
                   >
                     {tab}
                   </button>
               ))}
            </div>
        )}

        {/* Charts grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {charts.filter((c: CardConfig) => (c.tab || 'Geral') === activeTab).map((c: CardConfig) => (
            <div key={c.id} className={cn(
              c.width === 'full' ? 'col-span-1 lg:col-span-2 xl:col-span-3' : c.width === 'medium' ? 'col-span-1 lg:col-span-2 xl:col-span-2' : 'col-span-1',
              c.height === 'tall' ? 'row-span-2' : ''
            )}>
               <ChartCard card={c} chartDataResult={computeChartData(c)}
                 onEdit={() => onEditCard(c)} onRemove={() => onRemoveCard(c.id)} onDuplicate={() => onDuplicateCard(c.id)} onExport={() => onExportCard(c)}
                 showAuthor allFields={allFields} />
            </div>
          ))}
          <button
            onClick={onOpenBuilder}
            className="min-h-[280px] border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors"
          >
            <Plus size={24} />
            <span className="text-sm font-semibold">Adicionar gráfico</span>
            <span className="text-[11px] text-gray-400">Na aba atual...</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   KPI CARD
   ========================================================= */
function KPICard({ card, value, onEdit, onRemove, onDuplicate, onExport, accent = 'blue', compact, showAuthor }: any) {
  const cls = accent === 'blue'
    ? { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'text-blue-600', num: 'text-blue-900' }
    : { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', label: 'text-emerald-600', num: 'text-emerald-900' };
  const sub = card.calcType ? CALC_LABELS[card.calcType as KPICalcType] : '';
  return (
    <div className={cn("rounded-lg border p-3 relative group flex flex-col justify-between",
      compact ? "bg-white border-gray-200" : `${cls.bg} ${cls.border} min-w-[180px] shrink-0`)}>
      <div className="flex items-start justify-between gap-1">
        <span className={cn("text-[10px] font-bold uppercase tracking-wider truncate",
          compact ? 'text-gray-500' : cls.label)} title={card.title}>
          {card.title}
        </span>
        <CardActionMenu onEdit={onEdit} onRemove={onRemove} onDuplicate={onDuplicate} onExport={onExport} />
      </div>
      <div className={cn("font-mono font-bold truncate",
        compact ? "text-2xl text-gray-900" : `text-2xl ${cls.num}`)}>{value}</div>
      <div className="flex items-end justify-between gap-2 mt-0.5">
        {sub ? (
          <div className={cn("text-[9px] uppercase tracking-wide font-semibold",
            compact ? 'text-gray-400' : cls.label)}>{sub}</div>
        ) : <div />}
        {showAuthor && card.createdBy && <AuthorBadge name={card.createdBy} />}
      </div>
    </div>
  );
}

function AuthorBadge({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() || '')
    .join('');
  // Deterministic color based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash << 5) - hash + name.charCodeAt(i);
  const palette = [
    { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
    { bg: 'bg-blue-100', text: 'text-blue-700', ring: 'ring-blue-200' },
    { bg: 'bg-amber-100', text: 'text-amber-700', ring: 'ring-amber-200' },
    { bg: 'bg-rose-100', text: 'text-rose-700', ring: 'ring-rose-200' },
    { bg: 'bg-cyan-100', text: 'text-cyan-700', ring: 'ring-cyan-200' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700', ring: 'ring-indigo-200' },
    { bg: 'bg-teal-100', text: 'text-teal-700', ring: 'ring-teal-200' },
  ];
  const c = palette[Math.abs(hash) % palette.length];
  return (
    <span
      className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold ring-1 shrink-0",
        c.bg, c.text, c.ring)}
      title={`Criado por ${name}`}
      aria-label={`Criado por ${name}`}
    >
      <span className={cn("w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold",
        c.bg, c.text)}>{initials || '?'}</span>
      <span className="max-w-[70px] truncate">{name}</span>
    </span>
  );
}

function CardActionMenu({ onEdit, onRemove, onDuplicate, onExport }: any) {
  return (
    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
      <button onClick={onExport} className="p-1 text-gray-400 hover:text-gray-800 hover:bg-white/70 rounded" title="Exportar CSV (Auditoria)" aria-label="Exportar CSV">
        <Download size={12} />
      </button>
      <button onClick={onEdit} className="p-1 text-gray-400 hover:text-gray-800 hover:bg-white/70 rounded" title="Editar" aria-label="Editar card">
        <Pencil size={12} />
      </button>
      <button onClick={onDuplicate} className="p-1 text-gray-400 hover:text-gray-800 hover:bg-white/70 rounded" title="Duplicar" aria-label="Duplicar card">
        <Copy size={12} />
      </button>
      <button onClick={onRemove} className="p-1 text-gray-400 hover:text-red-600 hover:bg-white/70 rounded" title="Remover" aria-label="Remover card">
        <X size={12} />
      </button>
    </div>
  );
}

/* =========================================================
   CHART CARD
   ========================================================= */
function ChartCard({ card, chartDataResult, onEdit, onRemove, onDuplicate, onExport, showAuthor, allFields }: any) {
  const [labelPosition, setLabelPosition] = useState<'none' | 'top' | 'inside' | 'bottom'>('none');
  const [isExpanded, setIsExpanded] = useState(false);
  const data = chartDataResult?.data || [];
  const seriesKeys = chartDataResult?.seriesKeys || [];
  const Meta = VIZ_META[card.viz as ChartType] || VIZ_META.bar;
  const hasData = data && data.length > 0;

  const toggleLabel = () => {
     setLabelPosition(p => p === 'none' ? 'top' : p === 'top' ? 'bottom' : p === 'bottom' ? 'inside' : 'none');
  };

  const chartContent = (
    <div className="flex-1 min-h-0 w-full">
      {!hasData ? (
        <div className="h-full w-full flex flex-col items-center justify-center text-gray-400 text-xs gap-2">
          <Meta.icon size={28} className="text-gray-300" />
          Sem dados para os filtros atuais
        </div>
      ) : (
        <ErrorBoundary>
            <React.Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Carregando Gráfico...</div></div>}><ChartRenderer card={card} data={data} allFields={allFields} seriesKeys={seriesKeys} labelPosition={labelPosition} /></React.Suspense>
        </ErrorBoundary>
      )}
    </div>
  );

  if (isExpanded) {
     return (
       <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 md:p-8">
          <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col p-6">
              <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4 shrink-0">
                  <div className="font-bold text-xl text-gray-900">{card.title}</div>
                  <div className="flex items-center gap-2">
                      <button onClick={toggleLabel} className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-1.5 transition-colors">
                          <Hash size={14} /> Valores: {labelPosition === 'none' ? 'Ocultos' : labelPosition === 'top' ? 'Acima' : labelPosition === 'bottom' ? 'Abaixo' : 'No meio'}
                      </button>
                      <button onClick={() => setIsExpanded(false)} className="p-2 text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors">
                          <Minimize size={18} />
                      </button>
                  </div>
              </div>
              <div className="flex-1 w-full min-h-0">
                  {chartContent}
              </div>
          </div>
       </div>
     );
  }

  return (
    <div className={cn("bg-white border border-gray-200 rounded-xl p-4 flex flex-col group relative shadow-sm hover:shadow-md transition-shadow", card.height === 'tall' ? "h-[576px]" : "h-[280px]")}>
      <div className="flex items-start justify-between gap-2 mb-3 pb-2 border-b border-gray-100">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm text-gray-900 truncate" title={card.title}>{card.title}</div>
          <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1.5">
            <Meta.icon size={10} /> {Meta.label}
            {card.calcType && <><span>·</span> <span>{CALC_LABELS[card.calcType as KPICalcType]}</span></>}
          </div>
        </div>
        <div className="flex items-center gap-1">
            <button onClick={toggleLabel} className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors opacity-0 group-hover:opacity-100" title="Alternar valores no gráfico">
                <Hash size={14} />
            </button>
            <button onClick={() => setIsExpanded(true)} className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors opacity-0 group-hover:opacity-100" title="Expandir gráfico">
                <Maximize size={14} />
            </button>
            <CardActionMenu onEdit={onEdit} onRemove={onRemove} onDuplicate={onDuplicate} onExport={onExport} />
        </div>
      </div>
      {chartContent}
      {showAuthor && card.createdBy && (
        <div className="pt-2 mt-1 border-t border-gray-100 flex items-center justify-end">
          <AuthorBadge name={card.createdBy} />
        </div>
      )}
    </div>
  );
}




/* =========================================================
   CARD BUILDER MODAL
   ========================================================= */
function CardBuilder({ mode, initial, allFields, computeKPIValue, computeChartData, onClose, onSave, accentColors, existingTabs }: any) {
  const isMap = mode === 'map';
  const allowedViz: ChartType[] = isMap ? ['kpi'] : ['kpi', 'bar', 'pie', 'line', 'area', 'area3d', 'scatter', 'composed', 'table'];


  const [viz, setViz] = useState<ChartType>(initial?.viz || (isMap ? 'kpi' : 'bar'));
  const [title, setTitle] = useState(initial?.title || '');
  const [tab, setTab] = useState(initial?.tab || 'Geral');
  const [fieldId, setFieldId] = useState(initial?.fieldId || 'ROOT_COUNT');
  const [xAxisFieldId, setXAxisFieldId] = useState(initial?.xAxisFieldId || 'startedAt');
  const [yAxisFieldId, setYAxisFieldId] = useState(initial?.yAxisFieldId || 'ROOT_COUNT');
  const [zAxisFieldId, setZAxisFieldId] = useState(initial?.zAxisFieldId || '');
  const [groupAxisFieldId, setGroupAxisFieldId] = useState(initial?.groupAxisFieldId || 'respondentName');
  const [seriesAxisFieldId, setSeriesAxisFieldId] = useState(initial?.seriesAxisFieldId || '');
  const [dateGrouping, setDateGrouping] = useState<CardConfig['dateGrouping']>(initial?.dateGrouping || 'day');
  const [calcType, setCalcType] = useState<KPICalcType>(initial?.calcType || 'count');
  const [kpiMode, setKpiMode] = useState<any>(initial?.kpiMode || 'COUNT');
  const [aggregationType, setAggregationType] = useState<any>(initial?.aggregationType || 'SUM');
  const [isAggregated, setIsAggregated] = useState<boolean>(initial?.isAggregated ?? true);
  const [width, setWidth] = useState<'half'|'medium'|'full'>(initial?.width || 'half');
  const [height, setHeight] = useState<'normal'|'tall'>(initial?.height || 'normal');
  const [numberFormat, setNumberFormat] = useState<'decimal'|'time'>(initial?.numberFormat || 'decimal');
  const [pivotRowTotals, setPivotRowTotals] = useState<boolean>(initial?.pivotRowTotals ?? false);
  const [pivotColTotals, setPivotColTotals] = useState<boolean>(initial?.pivotColTotals ?? false);

  const ref = useClickOutside<HTMLDivElement>(onClose);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // auto-title suggestion
  useEffect(() => {
    if (initial?.title) return;
    if (!title || title === '' || title.startsWith('Sugerido:')) {
      let suggestion = '';
      if (viz === 'kpi') {
        if (fieldId === 'ROOT_COUNT') suggestion = 'Total de respostas';
        else {
          const f = allFields.find((x: any) => x.fieldId === fieldId);
          if (f) suggestion = `${CALC_LABELS[calcType]}: ${f.fieldLabel}`;
        }
      } else {
        const x = xAxisFieldId === 'startedAt' ? 'Data' : xAxisFieldId === 'respondentName' ? 'Respondente' : xAxisFieldId === 'formId' ? 'Formulário' : (allFields.find((f: any) => f.fieldId === xAxisFieldId)?.fieldLabel || '');
        const y = yAxisFieldId === 'ROOT_COUNT' ? 'Contagem' : (allFields.find((f: any) => f.fieldId === yAxisFieldId)?.fieldLabel || '');
        if (x && y) suggestion = `${y} por ${x}`;
      }
      if (suggestion) setTitle(suggestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viz, fieldId, xAxisFieldId, yAxisFieldId, calcType]);

  const canSave = title.trim() !== '' && (
    viz === 'kpi' ? !!fieldId : (!!xAxisFieldId && !!yAxisFieldId)
  );


  const preview: CardConfig = {
    id: 'preview', title, viz, tab,
    fieldId, calcType, xAxisFieldId, yAxisFieldId, zAxisFieldId, groupAxisFieldId, seriesAxisFieldId, dateGrouping,
    kpiMode, aggregationType, isAggregated, width, height, numberFormat, pivotRowTotals, pivotColTotals
  };

  const previewValue = viz === 'kpi' ? computeKPIValue(fieldId, calcType, numberFormat) : null;
  const previewChartDataResult = viz !== 'kpi' ? computeChartData(preview) : null;

  const numericFields = allFields.filter((f: any) => ['number', 'linear_scale', 'rating'].includes(f.fieldType));

  const getAvailableCalcTypes = (targetFieldId: string): KPICalcType[] => {
      if (targetFieldId === 'ROOT_COUNT') return ['count'];
      const fieldDef = allFields.find((f: any) => f.fieldId === targetFieldId);
      if (!fieldDef) return ['count', 'count_unique'];
      
      const type = fieldDef.fieldType;
      if (['number', 'linear_scale', 'rating'].includes(type)) {
          return ['sum', 'avg', 'max', 'min', 'median', 'count', 'count_unique'];
      }
      if (type === 'date') {
          return ['max', 'min', 'count', 'count_unique'];
      }
      return ['count', 'count_unique', 'mode'];
  };

  const availableCalcTypesKPI = getAvailableCalcTypes(fieldId);
  const availableCalcTypesChart = getAvailableCalcTypes(yAxisFieldId);

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog" aria-modal="true" aria-label="Novo card">
      <div ref={ref} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Sparkles size={16} className={accentColors.text} />
              {initial ? 'Editar card' : 'Novo card'}
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Escolha o tipo de visualização e os dados. O preview atualiza ao vivo.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded hover:bg-gray-100"
            aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-5 gap-0">
          {/* Left: config */}
          <div className="md:col-span-3 p-6 space-y-5 overflow-y-auto custom-scrollbar">

            {/* Step 0: Aba/Tab (Apenas para Analytics) */}
            {!isMap && (
               <div className="mb-6">
                  <StepLabel n={0} label="Aba de Agrupamento" />
                  <div className="space-y-2">
                    {existingTabs?.length > 0 && (
                        <select
                        value={existingTabs?.includes(tab) ? tab : '__new__'}
                        onChange={(e) => {
                            if (e.target.value === '__new__') {
                                setTab(''); // Clear for new input
                            } else {
                                setTab(e.target.value);
                            }
                        }}
                        className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all"
                        >
                        {existingTabs?.map((t: string) => <option key={t} value={t}>{t}</option>)}
                        <option value="__new__">+ Criar nova aba...</option>
                        </select>
                    )}

                    {(!existingTabs?.includes(tab) || existingTabs?.length === 0) ? (
                        <input
                          type="text"
                          value={tab}
                          onChange={(e) => setTab(e.target.value)}
                          placeholder="Digite o nome da nova aba..."
                          className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none transition-all placeholder:text-gray-400"
                        />
                    ) : null}
                  </div>
               </div>
            )}

            {/* Step 0.5: Dimensions */}
            <div className="mb-6">
              <StepLabel n={0.5} label="Dimensões do Card" />
              <div className="space-y-3">
                 <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Largura</label>
                    <div className="flex gap-2">
                       <button onClick={() => setWidth('half')} className={cn("flex-1 py-1.5 rounded border text-xs font-semibold", width === 'half' ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
                          Pequeno (33%)
                       </button>
                       <button onClick={() => setWidth('medium')} className={cn("flex-1 py-1.5 rounded border text-xs font-semibold", width === 'medium' ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
                          Médio (66%)
                       </button>
                       <button onClick={() => setWidth('full')} className={cn("flex-1 py-1.5 rounded border text-xs font-semibold", width === 'full' ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
                          Grande (100%)
                       </button>
                    </div>
                 </div>
                 {viz !== 'kpi' && (
                 <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Altura</label>
                    <div className="flex gap-2">
                       <button onClick={() => setHeight('normal')} className={cn("flex-1 py-1.5 rounded border text-xs font-semibold", height === 'normal' ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
                          1 Linha (Padrão)
                       </button>
                       <button onClick={() => setHeight('tall')} className={cn("flex-1 py-1.5 rounded border text-xs font-semibold", height === 'tall' ? "bg-blue-50 border-blue-400 text-blue-700" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
                          2 Linhas (Alto)
                       </button>
                    </div>
                 </div>
                 )}
              </div>
            </div>

            {/* Step 1: viz type */}
            <div>
              <StepLabel n={1} label="Tipo de visualização" />
              <div className="grid grid-cols-4 gap-2">
                {allowedViz.map(v => {
                  const m = VIZ_META[v];
                  const active = viz === v;
                  return (
                    <button
                      key={v}
                      onClick={() => setViz(v)}
                      className={cn(
                        "p-3 rounded-lg border text-center transition-all",
                        active
                          ? `${accentColors.bgLight} ${accentColors.border} ring-2 ring-offset-1 ${mode === 'map' ? 'ring-blue-400' : 'ring-emerald-400'}`
                          : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      )}
                    >
                      <m.icon size={22} className={cn("mx-auto mb-1.5", active ? accentColors.text : 'text-gray-500')} />
                      <div className={cn("text-[11px] font-semibold", active ? accentColors.text : 'text-gray-700')}>{m.label}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">{VIZ_META[viz].desc}</p>
            </div>

            {/* Step 2: data */}
            <div>
              <StepLabel n={2} label="Dados" />
              {viz === 'kpi' ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Pergunta</label>
                    <select value={fieldId} onChange={e => {
                      const newId = e.target.value;
                      setFieldId(newId);
                      const validCalcs = getAvailableCalcTypes(newId);
                      if (!validCalcs.includes(calcType)) {
                          setCalcType(validCalcs[0] || 'count');
                      }
                    }}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="ROOT_COUNT">Total de respostas</option>
                      <optgroup label="Perguntas">
                        {allFields.map((f: any) => <option key={f.fieldId} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  {fieldId !== 'ROOT_COUNT' && (
                    <div>
                      <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Cálculo</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {availableCalcTypesKPI.map(c => (
                          <button key={c} onClick={() => setCalcType(c)}
                            className={cn("text-[11px] py-1.5 px-2 rounded border font-medium transition-colors",
                              calcType === c
                                ? `${accentColors.bgLight} ${accentColors.border} ${accentColors.text}`
                                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50")}>
                            {CALC_LABELS[c]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Formato Visual</label>
                    <select value={numberFormat} onChange={e => setNumberFormat(e.target.value as any)}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="decimal">Decimal Padrão</option>
                      <option value="time">Duração (HH:MM)</option>
                    </select>
                  </div>

                </div>
              ) : (
                <div className="space-y-3">
                  {['line', 'bar', 'area', 'area3d', 'table'].includes(viz) && (
                    <div>
                      <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Quebrar linhas por (Série/Legenda)</label>
                      <select value={seriesAxisFieldId} onChange={e => setSeriesAxisFieldId(e.target.value)}
                        className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                        <option value="">Não quebrar (Linha única)</option>
                        <option value="respondentName">Respondente</option>
                        <option value="formId">Formulário</option>
                        <optgroup label="Perguntas">
                          {allFields.map((f: any) => <option key={`series-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                        </optgroup>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">{viz === 'scatter' ? 'Medir (eixo X)' : 'Agrupar por (eixo X)'}</label>
                    <select value={xAxisFieldId} onChange={e => setXAxisFieldId(e.target.value)}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      {viz !== 'scatter' && (
                        <>
                          <option value="startedAt">Data da resposta</option>
                          <option value="respondentName">Respondente</option>
                          <option value="formId">Formulário</option>
                        </>
                      )}
                      <optgroup label={viz === 'scatter' ? "Perguntas numéricas" : "Perguntas"}>
                        {(viz === 'scatter' ? numericFields : allFields).map((f: any) => <option key={`x-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  {xAxisFieldId === 'startedAt' && (
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Período da Data</label>
                    <select value={dateGrouping} onChange={e => setDateGrouping(e.target.value as any)}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="day">Dia</option>
                      <option value="week">Semana</option>
                      <option value="month">Mês</option>
                      <option value="bimonth">Bimestre</option>
                      <option value="semester">Semestre</option>
                      <option value="year">Ano</option>
                    </select>
                  </div>
                  )}

                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Medir (eixo Y)</label>
                    <select value={yAxisFieldId} onChange={e => {
                      const newId = e.target.value;
                      setYAxisFieldId(newId);
                      const validCalcs = getAvailableCalcTypes(newId);
                      if (!validCalcs.includes(calcType)) {
                          setCalcType(validCalcs[0] || 'count');
                      }
                    }}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="ROOT_COUNT">Número de respostas</option>
                      <optgroup label="Todas as Perguntas">
                        {allFields.map((f: any) => <option key={`y-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  {viz === 'scatter' && (
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Tamanho da bolha (eixo Z/Raio)</label>
                    <select value={zAxisFieldId} onChange={e => {
                      setZAxisFieldId(e.target.value);
                    }}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="">Número de respostas (Padrão)</option>
                      <optgroup label="Perguntas numéricas">
                        {numericFields.map((f: any) => <option key={`z-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  )}
                  {viz === 'scatter' && (
                  <>
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Tamanho da bolha (eixo Z/Raio)</label>
                    <select value={zAxisFieldId} onChange={e => {
                      setZAxisFieldId(e.target.value);
                    }}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="">Número de respostas (Padrão)</option>
                      <optgroup label="Perguntas numéricas">
                        {numericFields.map((f: any) => <option key={`z-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Fator Agrupador (Esferas)</label>
                    <select value={groupAxisFieldId} onChange={e => setGroupAxisFieldId(e.target.value)}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="respondentName">Respondente</option>
                      <option value="formId">Formulário</option>
                      <optgroup label="Perguntas">
                        {allFields.map((f: any) => <option key={`g-${f.fieldId}`} value={f.fieldId}>[{f.formTitle.slice(0, 12)}] {f.fieldLabel}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  </>
                  )}
                  {yAxisFieldId !== 'ROOT_COUNT' && (
                    <div>
                      <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Como calcular</label>
                      <div className="grid grid-cols-3 gap-1.5">
                        {availableCalcTypesChart.map(c => (
                          <button key={c} onClick={() => setCalcType(c)}
                            className={cn("text-[11px] py-1.5 px-2 rounded border font-medium transition-colors",
                              calcType === c
                                ? `${accentColors.bgLight} ${accentColors.border} ${accentColors.text}`
                                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50")}>
                            {CALC_LABELS[c]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700 mb-1 block">Formato Visual</label>
                    <select value={numberFormat} onChange={e => setNumberFormat(e.target.value as any)}
                      className="w-full text-sm p-2 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400">
                      <option value="decimal">Decimal Padrão</option>
                      <option value="time">Duração (HH:MM)</option>
                    </select>
                  </div>
                  {viz === 'table' && (
                    <div className="pt-2 border-t border-gray-100 space-y-2 mt-2">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="pivotRowTotals" checked={pivotRowTotals} onChange={e => setPivotRowTotals(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5" />
                        <label htmlFor="pivotRowTotals" className="text-[11px] font-semibold text-gray-700 cursor-pointer">Mostrar Total da Linha</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="pivotColTotals" checked={pivotColTotals} onChange={e => setPivotColTotals(e.target.checked)} className="rounded text-blue-600 focus:ring-blue-500 w-3.5 h-3.5" />
                        <label htmlFor="pivotColTotals" className="text-[11px] font-semibold text-gray-700 cursor-pointer">Mostrar Total da Coluna</label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 3: title */}
            <div>
              <StepLabel n={3} label="Título" />
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Ex: Abastecimentos por operador"
                className="w-full text-sm p-2.5 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
              />
            </div>
          </div>

          {/* Right: preview */}
          <div className="md:col-span-2 bg-gray-50 p-6 border-l border-gray-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
              <Eye size={12} /> Preview ao vivo
            </div>
            {viz === 'kpi' ? (
              <div className={cn("rounded-lg border p-4",
                mode === 'map' ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200")}>
                <div className={cn("text-[10px] font-bold uppercase tracking-wider mb-2 truncate",
                  mode === 'map' ? "text-blue-600" : "text-emerald-600")}>
                  {title || 'Seu card'}
                </div>
                <div className={cn("font-mono font-bold text-3xl",
                  mode === 'map' ? "text-blue-900" : "text-emerald-900")}>
                  {previewValue}
                </div>
                {calcType && fieldId !== 'ROOT_COUNT' && (
                  <div className={cn("text-[9px] uppercase tracking-wide font-semibold mt-1",
                    mode === 'map' ? "text-blue-600" : "text-emerald-600")}>
                    {CALC_LABELS[calcType]}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-3 h-[260px] flex flex-col">
                <div className="text-xs font-semibold text-gray-800 mb-2 pb-1.5 border-b border-gray-100 truncate">
                  {title || 'Seu gráfico'}
                </div>
                <div className="flex-1 min-h-0">
                  {previewChartDataResult?.data && previewChartDataResult.data.length > 0 ? (
                    <ErrorBoundary>

                        <React.Suspense fallback={<div className="w-full h-full flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Carregando Gráfico...</div></div>}><ChartRenderer card={preview} data={previewChartDataResult.data} allFields={allFields} seriesKeys={previewChartDataResult?.seriesKeys || []} /></React.Suspense>

                    </ErrorBoundary>
                  ) : (
                    <div className="h-full flex items-center justify-center text-xs text-gray-400 text-center px-4">
                      Sem dados para os filtros atuais
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
              O preview respeita os filtros ativos na página. Ajuste os filtros para ver diferentes recortes.
            </p>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between shrink-0 bg-gray-50">
          <button onClick={onClose} className="text-xs text-gray-600 hover:text-gray-900 font-medium px-3 py-2">
            Cancelar
          </button>
          <button

            onClick={() => {
              const card: Omit<CardConfig, 'id'> = viz === 'kpi'
                ? { title, viz, tab, fieldId, calcType, width, height, numberFormat }
                : { title, viz, tab, xAxisFieldId, yAxisFieldId, calcType, zAxisFieldId, groupAxisFieldId, seriesAxisFieldId, dateGrouping, width, height, numberFormat, pivotRowTotals, pivotColTotals };
              onSave(card);
            }}
            disabled={!canSave}
            className={cn(
              "text-sm font-semibold px-5 py-2 rounded-lg flex items-center gap-1.5 transition-colors text-white",
              canSave
                ? `${accentColors.bg} ${accentColors.hover}`
                : "bg-gray-300 cursor-not-allowed"
            )}
          >
            {initial ? 'Salvar alterações' : 'Criar card'} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function StepLabel({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-5 h-5 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center">{n}</div>
      <span className="text-xs font-bold uppercase tracking-wider text-gray-700">{label}</span>
    </div>
  );
}

/* =========================================================
   IMPORT CHOICE DIALOG
   ========================================================= */
function ImportChoiceDialog({ payload, currentCount, onCancel, onApply, accentColors, existingTabs }: any) {
  const ref = useClickOutside<HTMLDivElement>(onCancel);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onCancel]);

  const [importTab, setImportTab] = useState<string>('Manter as do arquivo');

  const incomingCount = Array.isArray(payload.cards) ? payload.cards.length : 0;
  const exporter = payload.exportedBy?.name || 'Desconhecido';
  const exportedAt = payload.exportedAt ? format(new Date(payload.exportedAt), "dd/MM/yyyy 'às' HH:mm") : '—';
  const modeLabel = payload.mode === 'map' ? 'Mapa Operacional' : 'Analítico & Gráficos';

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog" aria-modal="true" aria-label="Importar configurações">
      <div ref={ref} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Upload size={16} className={accentColors.text} />
            Importar backup
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Escolha como deseja combinar o conteúdo importado com o que já está na tela.
          </p>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-2 gap-3 text-[11px]">
            <div>
              <div className="text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Origem</div>
              <div className="text-gray-800 font-medium truncate" title={modeLabel}>{modeLabel}</div>
            </div>
            <div>
              <div className="text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Exportado por</div>
              <div className="text-gray-800 font-medium truncate" title={exporter}>{exporter}</div>
            </div>
            <div>
              <div className="text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Data</div>
              <div className="text-gray-800 font-medium">{exportedAt}</div>
            </div>
            <div>
              <div className="text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Cards no arquivo</div>
              <div className="text-gray-800 font-medium">{incomingCount}</div>
            </div>
          </div>

          {payload.mode !== 'map' && (
            <div className="mb-4">
              <label className="block text-[11px] font-bold text-gray-700 uppercase tracking-wide mb-1.5">Em qual aba importar?</label>
              <select
                value={importTab}
                onChange={e => setImportTab(e.target.value)}
                className="w-full text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                 <option value="Manter as do arquivo">Manter as abas definidas no arquivo</option>
                 <option value="Nova Aba (Importação)">Nova Aba: "Importação"</option>
                 {existingTabs?.map((t: string) => (
                    <option key={t} value={t}>Na Aba: {t}</option>
                 ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={() => onApply('merge', importTab)}
              className="w-full text-left p-3 border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-lg transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Plus size={16} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-900">Adicionar aos existentes</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    Mantém seus <strong>{currentCount}</strong> card{currentCount === 1 ? '' : 's'} atuais e acrescenta mais <strong>{incomingCount}</strong> do arquivo. Os filtros atuais continuam como estão.
                  </div>
                </div>
              </div>
            </button>

            <button
              onClick={() => {
                if (currentCount === 0 || window.confirm(`Isto vai apagar seus ${currentCount} card(s) atuais e aplicar apenas o que está no arquivo. Continuar?`)) {
                  onApply('replace', importTab);
                }
              }}
              className="w-full text-left p-3 border border-gray-200 hover:border-red-400 hover:bg-red-50 rounded-lg transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <AlertTriangle size={16} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-900">Limpar e importar</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    Remove os <strong>{currentCount}</strong> card{currentCount === 1 ? '' : 's'} atuais e aplica o backup por inteiro (cards, filtros e configurações). Não pode ser desfeito.
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-end bg-gray-50">
          <button onClick={onCancel} className="text-xs text-gray-600 hover:text-gray-900 font-medium px-3 py-2">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   FORENSIC MODAL (preserved)
   ========================================================= */
function ForensicModal({ detail, onClose }: any) {
  const ref = useClickOutside<HTMLDivElement>(onClose);
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const renderFieldValue = (field: any, value: any) => {
    if (value === undefined || value === null || value === '') return <em className="text-gray-400 font-normal">Não preenchido</em>;
    if (field.type === 'media_photo' || field.type === 'signature') {
      const urls = Array.isArray(value) ? value : [value];
      return (
        <div className="flex gap-2 flex-wrap mt-1">
          {urls.map((u, i) => (
            <div key={i} className="h-16 w-16 bg-gray-100 rounded border border-gray-200 overflow-hidden flex items-center justify-center">
              {String(u).startsWith('http') || String(u).startsWith('data:') ? (
                <img src={String(u)} alt="anexo" className="h-full w-full object-cover" />
              ) : (
                <span className="text-[9px] text-gray-400 text-center px-1 break-all">{String(u)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }
    if (field.type === 'matrix') {
      if (!Array.isArray(value)) return <span className="font-medium text-gray-800 break-words">{JSON.stringify(value)}</span>;
      return (
        <div className="mt-1 flex flex-col gap-1 border border-gray-200 rounded p-1.5 bg-white">
          {value.map((row: any, idx: number) => (
            <div key={idx} className="text-xs text-gray-700 flex flex-wrap gap-2 p-1.5 bg-gray-50 rounded">
              {Object.entries(row).map(([k, v]) => (
                <span key={k} className="bg-white px-1.5 py-0.5 rounded border border-gray-100 shadow-sm">
                  <strong>{k}:</strong> {String(v)}
                </span>
              ))}
            </div>
          ))}
        </div>
      );
    }
    if (typeof value === 'boolean') return <span className="font-medium text-gray-800">{value ? 'Sim' : 'Não'}</span>;
    return <span className="font-medium text-gray-800 break-words">{String(value)}</span>;
  };

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
      role="dialog" aria-modal="true" aria-label="Detalhes da resposta">
      <div ref={ref} className="bg-white border border-gray-200 w-full max-w-xl max-h-[85vh] flex flex-col shadow-2xl rounded-xl overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-blue-50 to-white flex justify-between items-center shrink-0 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-blue-600 text-white p-2.5 rounded-lg shadow-md shrink-0">
              <FileText size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-extrabold text-gray-900 leading-tight">Detalhes da resposta</h3>
              <span className="text-[10px] text-blue-600 uppercase tracking-widest font-bold bg-blue-100 px-2 py-0.5 rounded mt-1 inline-block border border-blue-200 truncate max-w-[260px]">
                {detail.record.id}
              </span>
            </div>
          </div>
          <button onClick={onClose}
            className="text-gray-400 hover:text-red-600 bg-gray-50 hover:bg-red-50 p-2.5 rounded-full transition-colors border border-gray-100"
            title="Fechar" aria-label="Fechar modal">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          <div className="mb-6 bg-slate-50 rounded-lg p-5 grid grid-cols-2 gap-y-5 gap-x-6 text-xs border border-slate-200">
            <div>
              <span className="text-slate-400 font-bold uppercase tracking-wider block mb-1.5 text-[9px]">Formulário</span>
              <span className="font-semibold text-slate-800 block leading-tight">
                {detail.form?.title}
                <span className="text-slate-400 font-normal border border-slate-300 rounded px-1 ml-1 text-[9px]">v{detail.record.formVersion}</span>
              </span>
            </div>
            <div>
              <span className="text-slate-400 font-bold uppercase tracking-wider block mb-1.5 text-[9px]">Respondente</span>
              <span className="font-semibold text-slate-800 block leading-tight bg-white border border-slate-200 px-2 py-1 rounded w-fit">{detail.record.respondentName}</span>
            </div>
            <div>
              <span className="text-slate-400 font-bold uppercase tracking-wider block mb-1.5 text-[9px]">Início</span>
              <span className="font-semibold text-slate-600 block">{format(new Date(detail.record.startedAt), "dd/MM/yy 'às' HH:mm:ss")}</span>
            </div>
            <div>
              <span className="text-slate-400 font-bold uppercase tracking-wider block mb-1.5 text-[9px]">Envio</span>
              <span className="font-semibold text-blue-700 block">{format(new Date(detail.record.submittedAt), "dd/MM/yy 'às' HH:mm:ss")}</span>
            </div>
            {detail.record.deviceInfo?.geolocation && (
              <div className="col-span-2 pt-3 border-t border-slate-200">
                <span className="text-slate-400 font-bold uppercase tracking-wider block mb-2 text-[9px] flex items-center gap-1">
                  <MapPin size={10} /> Coordenadas
                </span>
                <span className="font-mono text-[11px] text-blue-800 font-semibold bg-blue-100/50 px-3 py-1.5 rounded-md block w-fit border border-blue-200 flex items-center gap-2">
                  {detail.record.deviceInfo.geolocation.lat}, {detail.record.deviceInfo.geolocation.lng}
                </span>
              </div>
            )}
          </div>

          <h4 className="text-[11px] font-extrabold text-gray-500 uppercase tracking-widest mb-4 pb-2 border-b border-gray-100">
            Respostas capturadas
          </h4>
          <div className="space-y-3">
            {detail.version?.schema.map((field: any) => {
              const answer = detail.record.data?.[field.id]?.value;
              return (
                <div key={field.id} className="bg-white rounded-lg p-4 border border-gray-200">
                  <span className="block text-[10px] font-bold text-blue-600 mb-2 uppercase tracking-wider">
                    {field.label}
                  </span>
                  <div className="text-[13px] pl-3 border-l-[3px] border-gray-100 text-gray-800">
                    {renderFieldValue(field, answer)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
